<div
  class="ai-help-backdrop backdrop"
  [class.ai-help-backdrop--visible]="state.isOpen()"
  (click)="onClose()"
  aria-hidden="true"
></div>

<div
  class="ai-help-shell"
  [class.ai-help-shell--open]="state.isOpen()"
  [class.ai-help-shell--expanded]="isExpanded() || (state.isOpen() && isMobileViewport())"
  [class.ai-help-shell--with-prompts]="state.isOpen() && isSamplePromptsOpen()"
  [class.ai-help-shell--with-history]="state.isOpen() && isHistoryOpen()"
  [class.ai-help-shell--fall-of-summer]="eventTheme === 'fall-of-summer'"
  [class.ai-help-shell--thanksgiving]="eventTheme === 'thanksgiving'"
  [class.ai-help-shell--halloween]="eventTheme === 'halloween'"
  [class.ai-help-shell--remembrance]="eventTheme === 'remembrance'"
>
<aside
  class="ai-help-prompts-panel"
  [class.ai-help-prompts-panel--open]="state.isOpen() && isSamplePromptsOpen()"
  [attr.aria-hidden]="!(state.isOpen() && isSamplePromptsOpen())"
  role="complementary"
  aria-label="Sample Prompts"
>
  <div class="ai-help-prompts-panel__container">
    <header class="ai-help-prompts-panel__header">
      <div class="ai-help-prompts-panel__heading">
        <h2 class="ai-help-prompts-panel__title">{{ samplePromptsChrome().title }}</h2>
        <p class="ai-help-prompts-panel__subtitle">{{ samplePromptsChrome().subtitle }}</p>
      </div>

      <div class="ai-help-prompts-panel__header-actions">
        <div class="ai-help-prompts-panel__search search-box">
          <i class="wl_icon_Search" aria-hidden="true"></i>
          <input
            type="search"
            class="ai-help-prompts-panel__search-input"
            [placeholder]="samplePromptsChrome().searchPlaceholder"
            [ngModel]="samplePromptsQuery()"
            (ngModelChange)="onSamplePromptsQueryChange($event)"
            aria-label="Search questions"
          />
        </div>
        <button
          type="button"
          class="ai-help-prompts-panel__close"
          matTooltip="Close"
          matTooltipAlways
          aria-label="Close Sample Prompts"
          (click)="onCloseSamplePrompts()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </header>

    <div class="ai-help-prompts-panel__body" #promptsPanelBody>
      @if (state.isPromptExamplesLoading() && !filteredSampleCategories().length) {
        <div class="ai-help-prompts-panel__loading" role="status" aria-live="polite">
          <app-spinner [size]="22"></app-spinner>
          <span>Loading questions…</span>
        </div>
      } @else if (state.promptExamplesError() && !filteredSampleCategories().length) {
        <div class="ai-help-prompts-panel__error" role="alert">
          <p class="ai-help-prompts-panel__error-text">
            Couldn’t load questions. Please try again.
          </p>
          <button
            type="button"
            class="ai-help-prompts-panel__retry"
            (click)="onRetryPromptExamples()"
          >
            Retry
          </button>
        </div>
      } @else {
        @for (category of filteredSampleCategories(); track category.id) {
          <section
            class="ai-help-prompts-panel__category"
            [attr.data-tone]="category.tone"
            [attr.data-category-id]="category.id"
            [class.ai-help-prompts-panel__category--expanded]="isSampleCategoryExpanded(category.id)"
            [class.ai-help-prompts-panel__category--highlighted]="highlightedCategoryId() === category.id"
          >
            <div class="ai-help-prompts-panel__category-header">
              <span class="ai-help-prompts-panel__category-icon" aria-hidden="true">
                <mat-icon>{{ category.icon }}</mat-icon>
              </span>
              <h3 class="ai-help-prompts-panel__category-title">{{ category.title }}</h3>
            </div>

            <p class="ai-help-prompts-panel__category-desc">{{ category.description }}</p>

            <div class="ai-help-prompts-panel__category-list">
              @for (item of visibleSamplePromptItems(category); track samplePromptItemTrackId(item, $index)) {
                <button
                  type="button"
                  class="ai-help-prompts-panel__prompt"
                  (click)="onSamplePromptClick(item.prompt, $event)"
                >
                  <span class="ai-help-prompts-panel__prompt-label">{{ item.label }}</span>
                  <mat-icon class="ai-help-prompts-panel__prompt-chevron" aria-hidden="true"
                    >chevron_right</mat-icon
                  >
                </button>
              }
            </div>

            @if (canExpandSampleCategory(category)) {
              <button
                type="button"
                class="ai-help-prompts-panel__more"
                [attr.aria-expanded]="isSampleCategoryExpanded(category.id)"
                (click)="onToggleSampleCategory(category.id)"
              >
                @if (!isSampleCategoryExpanded(category.id)) {
                  <span class="ai-help-prompts-panel__more-plus" aria-hidden="true">+</span>
                }
                {{ sampleCategoryMoreLabel(category) }}
              </button>
            }
          </section>
        } @empty {
          <p class="ai-help-prompts-panel__empty">
            {{ samplePromptsQuery().trim() ? 'No matching prompts.' : 'No questions available.' }}
          </p>
        }
      }
    </div>
  </div>
</aside>

<aside
  class="ai-help-history-panel"
  [class.ai-help-history-panel--open]="state.isOpen() && isHistoryOpen()"
  [attr.aria-hidden]="!(state.isOpen() && isHistoryOpen())"
  role="complementary"
  aria-label="Question history"
>
  <div class="ai-help-history-panel__container">
    <header class="ai-help-history-panel__header">
      <div class="ai-help-history-panel__heading">
        <h2 class="ai-help-history-panel__title">Question history</h2>
        <p class="ai-help-history-panel__subtitle">Open a past conversation</p>
      </div>
      <div class="ai-help-history-panel__header-actions">
        @if (historyConversations().length) {
          <button
            type="button"
            class="ai-help-history-panel__delete-all"
            matTooltip="Delete all"
            matTooltipAlways
            aria-label="Delete all conversations"
            [disabled]="state.isDeletingAllConversations()"
            (click)="onHistoryDeleteAll($event)"
          >
            <mat-icon aria-hidden="true">delete_sweep</mat-icon>
          </button>
        }
        <button
          type="button"
          class="ai-help-history-panel__close"
          matTooltip="Close"
          matTooltipAlways
          aria-label="Close question history"
          (click)="onCloseHistory()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </header>

    <div class="ai-help-history-panel__body">
      @if (state.conversations() === null) {
        <div class="ai-help-history-panel__loading" role="status" aria-live="polite">
          <app-spinner [size]="22"></app-spinner>
          <span>Loading history…</span>
        </div>
      } @else if (!historyConversations().length) {
        <div class="ai-help-history-panel__empty">
          <span class="ai-help-history-panel__empty-icon" aria-hidden="true">
            <mat-icon>history</mat-icon>
          </span>
          <p class="ai-help-history-panel__empty-title">No past questions yet</p>
          <p class="ai-help-history-panel__empty-copy">
            Conversations you start will show up here.
          </p>
        </div>
      } @else {
        <ul class="ai-help-history-panel__list" role="list">
          @for (item of historyConversations(); track item.conversationId) {
            <li
              class="ai-help-history-panel__row"
              [class.ai-help-history-panel__row--active]="
                state.activeConversationId() === item.conversationId
              "
            >
              <button
                type="button"
                class="ai-help-history-panel__item"
                [attr.aria-current]="
                  state.activeConversationId() === item.conversationId ? 'true' : null
                "
                (click)="onHistoryItemClick(item)"
              >
                <span class="ai-help-history-panel__item-icon" aria-hidden="true">
                  <mat-icon>chat_bubble_outline</mat-icon>
                </span>
                <span class="ai-help-history-panel__item-copy">
                  <span class="ai-help-history-panel__item-title">{{
                    item.title || 'Untitled question'
                  }}</span>
                  @if (formatHistoryTimestamp(item.updatedAt || item.createdAt); as when) {
                    <span class="ai-help-history-panel__item-time">{{ when }}</span>
                  }
                </span>
                <mat-icon class="ai-help-history-panel__item-chevron" aria-hidden="true"
                  >chevron_right</mat-icon
                >
              </button>
              <button
                type="button"
                class="ai-help-history-panel__delete"
                matTooltip="Delete"
                matTooltipAlways
                [attr.aria-label]="'Delete ' + (item.title || 'conversation')"
                [disabled]="
                  state.isDeletingConversation(item.conversationId) ||
                  state.isDeletingAllConversations()
                "
                (click)="onHistoryItemDelete(item, $event)"
              >
                <mat-icon aria-hidden="true">delete_outline</mat-icon>
              </button>
            </li>
          }
        </ul>
      }
    </div>
  </div>
</aside>

<div
  class="ai-help-panel slide-panel"
  [class.ai-help-panel--open]="state.isOpen()"
  [class.ai-help-panel--expanded]="isExpanded() || (state.isOpen() && isMobileViewport())"
  role="complementary"
  aria-label="WorkLuge Assistant"
>
  <div class="modal-container">
    <header class="modal-header">
      @if (state.isOpen() && eventTheme === 'fall-of-summer') {
        <!-- <img
          class="modal-header__scene modal-header__scene--fall"
          src="assets/images/events/fall-of-summer.png"
          alt=""
          aria-hidden="true"
          draggable="false"
        /> -->
      }
      @if (state.isOpen() && eventTheme === 'remembrance') {
        <!-- <img
          class="modal-header__scene modal-header__scene--remembrance"
          src="assets/images/events/canada-banner.png"
          alt=""
          aria-hidden="true"
          draggable="false"
        /> -->
      }
      @if (state.isOpen() && eventTheme === 'halloween') {
        <img
          class="modal-header__web"
          src="assets/images/events/hallo-sp.gif"
          alt=""
          aria-hidden="true"
          draggable="false"
        />
        <div class="modal-header__bats" aria-hidden="true">
          @for (bat of headerEventBats; track $index) {
            <img
              class="modal-header__bat"
              [class.modal-header__bat--a]="bat.path === 'a'"
              [class.modal-header__bat--b]="bat.path === 'b'"
              [class.modal-header__bat--c]="bat.path === 'c'"
              src="assets/images/events/halloween-ybat.gif"
              alt=""
              draggable="false"
              [style.--bat-left]="bat.left"
              [style.--bat-top]="bat.top"
              [style.--bat-size]="bat.size"
              [style.--bat-duration]="bat.duration"
              [style.--bat-delay]="bat.delay"
            />
          }
        </div>
      }
      @if (state.isOpen() && eventTheme === 'remembrance') {
        <div class="modal-header__leaves" aria-hidden="true">
          @for (petal of headerEventPoppies; track $index) {
            <img
              class="modal-header__leaf"
              [src]="petal.src"
              alt=""
              draggable="false"
              [style.--leaf-left]="petal.left"
              [style.--leaf-size]="petal.size"
              [style.--leaf-duration]="petal.duration"
              [style.--leaf-delay]="petal.delay"
              [style.--leaf-drift]="petal.drift"
              [style.--leaf-spin]="petal.spin"
            />
          }
        </div>
      }
      @if (state.isOpen() && (eventTheme === 'thanksgiving' || eventTheme === 'fall-of-summer')) {
        <div class="modal-header__leaves" aria-hidden="true">
          @for (leaf of headerEventLeaves; track $index) {
            <img
              class="modal-header__leaf"
              [src]="leaf.src"
              alt=""
              draggable="false"
              [style.--leaf-left]="leaf.left"
              [style.--leaf-size]="leaf.size"
              [style.--leaf-duration]="leaf.duration"
              [style.--leaf-delay]="leaf.delay"
              [style.--leaf-drift]="leaf.drift"
              [style.--leaf-spin]="leaf.spin"
            />
          }
        </div>
      }
      <div class="modal-header__title">
        <span
          class="tim-name-tip tim-name-tip--below"
          [attr.aria-label]="timHoverLabel"
          (mouseenter)="onTimNameTipEnter($event)"
          (mouseleave)="onTimNameTipLeave()"
          (focusin)="onTimNameTipEnter($event)"
          (focusout)="onTimNameTipLeave()"
          tabindex="0"
        >
          <img
            class="modal-header__icon"
            [src]="headerTimIconSrc"
            alt=""
            draggable="false"
          />
        </span>
        <div class="modal-header__copy">
          <div class="modal-header__heading">
            <h2>WorkLuge Assistant</h2>
            <span class="modal-header__beta" aria-label="Beta">beta</span>
          </div>
          <!-- <p class="modal-header__subtitle">Your Workspace Assistant</p> -->
          <div class="modal-header__ready" aria-label="Status: Ready">
            <p class="powered-by">Powered by AI</p>
            <!-- <span class="modal-header__status" aria-hidden="true"></span> -->
            <!-- <span class="modal-header__ready-label">Ready</span> -->
          </div>
          <a
            class="modal-header__how-i-work"
            [href]="howIWorkPageUrl"
            target="_blank"
            rel="noopener noreferrer"
            role="link"
            aria-label="How I work? Opens a short video about TIM, TechWyse Intelligent Manager, in a new tab"
            (click)="onHowIWorkClick($event)"
          >
            <mat-icon aria-hidden="true">videocam</mat-icon>
            <span>How I work?</span>
          </a>
        </div>
      </div>
      <div class="modal-header__actions">
        <button
          type="button"
          class="modal-header__sample-btn"
          [class.modal-header__sample-btn--active]="isSamplePromptsOpen()"
          [attr.aria-expanded]="isSamplePromptsOpen()"
          [attr.aria-pressed]="isSamplePromptsOpen()"
          aria-label="Sample Products"
          (click)="onToggleSamplePrompts($event)"
        >
          <mat-icon aria-hidden="true">auto_awesome</mat-icon>
          <span>Sample Prompts</span>
        </button>
        <button
          type="button"
          class="header-action-btn"
          [class.header-action-btn--active]="isHistoryOpen()"
          [attr.aria-expanded]="isHistoryOpen()"
          [attr.aria-pressed]="isHistoryOpen()"
          matTooltip="Question history"
          matTooltipAlways
          aria-label="Question history"
          (click)="onToggleHistory($event)"
        >
          <mat-icon>history</mat-icon>
        </button>
        @if (!state.isHomeView() && state.hasMessages()) {
          <button
            type="button"
            class="header-action-btn"
            matTooltip="Back to home"
            matTooltipAlways
            aria-label="Back to home"
            (click)="onBackToHome()"
          >
            <mat-icon>arrow_back</mat-icon>
          </button>
        } @else if (state.isHomeView() && state.hasMessages()) {
          <button
            type="button"
            class="header-action-btn"
            matTooltip="Return to previous thread"
            matTooltipAlways
            aria-label="Return to previous thread"
            (click)="onReturnToThread()"
          >
            <mat-icon>arrow_forward</mat-icon>
          </button>
        }
        <button
          type="button"
          class="header-action-btn"
          matTooltip="New chat"
          matTooltipAlways
          aria-label="New chat"
          (click)="onNewChat()"
        >
          <mat-icon>add</mat-icon>
        </button>
        @if (!isMobileViewport()) {
        <button
          type="button"
          class="header-action-btn header-action-btn--expand"
          [class.header-action-btn--expanded]="isExpanded()"
          [matTooltip]="isExpanded() ? 'Exit full height' : 'Expand to full height'"
          matTooltipAlways
          [attr.aria-label]="isExpanded() ? 'Exit full height panel' : 'Expand panel to full height'"
          [attr.aria-pressed]="isExpanded()"
          (click)="onToggleExpand()"
        >
          <span class="expand-glyph" aria-hidden="true">
            @if (isExpanded()) {
              <!-- Collapse arrows: inward corners -->
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3v5H3M16 3v5h5M8 21v-5H3M16 21v-5h5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            } @else {
              <!-- Expand arrows: outward corners -->
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8V3h5M21 8V3h-5M3 16v5h5M21 16v5h-5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            }
          </span>
        </button>
        }
        <button
          type="button"
          class="close-btn"
          matTooltip="Close"
          matTooltipAlways
          aria-label="Close WorkLuge Assistant"
          (click)="onClose()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </header>

    <div class="modal-content-wrapper" #messageList>
      <div class="modal-content">
        @if (state.isHomeView()) {
          <section class="ai-help-panel__home">
            <div class="ai-help-panel__home-greeting">
              <h3 class="ai-help-panel__home-greeting-title">
                <span class="ai-help-panel__home-greeting-text">
                  {{ greetingTitle() }},
                  <span class="ai-help-panel__home-greeting-name">{{ greetingName() }}</span>!
                </span>
                <span class="ai-help-panel__home-greeting-emoji" aria-hidden="true">
                  <img
                    class="ai-help-panel__home-greeting-robot"
                    src="assets/images/hand_wave.gif"
                    alt=""
                    width="32"
                    height="32"
                    decoding="async"
                    draggable="false"
                  />
                </span>
              </h3>
              <p class="ai-help-panel__home-greeting-subtitle">{{ home.subtitle }}</p>
            </div>

            <div class="ai-help-panel__home-actions-grid" aria-label="Quick actions">
              @for (card of home.actions; track card.id) {
                <button
                  type="button"
                  class="ai-help-panel__home-action-card"
                  (click)="onHomeActionClick(card)"
                  [class.ai-help-panel__home-action-card--active]="isHomeActionHighlighted(card.id)"
                  [attr.data-tone]="card.tone"
                >
                  <span class="ai-help-panel__home-action-accent" aria-hidden="true"></span>
                  <span
                    class="ai-help-panel__home-action-icon"
                    [attr.data-tone]="card.tone"
                    aria-hidden="true"
                  >
                    <mat-icon>{{ card.icon }}</mat-icon>
                  </span>
                  <span class="ai-help-panel__home-action-body">
                    <span class="ai-help-panel__home-action-label">{{ card.label }}</span>
                    <span class="ai-help-panel__home-action-description">{{ card.description }}</span>
                    <span class="ai-help-panel__home-action-quote">{{ card.quote }}</span>
                  </span>
                </button>
              }
            </div>

            @if (state.attention(); as attention) {
              <div class="ai-help-panel__home-attention">
                <div class="ai-help-panel__home-attention-header">
                  <h4 class="ai-help-panel__home-attention-title">{{ attention.title }}</h4>
                </div>

                <div class="ai-help-panel__home-attention-items">
                  @for (item of attention.items; track item.id) {
                    <button
                      type="button"
                      class="ai-help-panel__home-attention-item"
                      [attr.data-tone]="item.tone"
                      (click)="onAttentionItemClick(item.prompt)"
                    >
                      <span class="ai-help-panel__home-attention-item-icon" aria-hidden="true">
                        <mat-icon>{{ item.icon }}</mat-icon>
                      </span>
                      @if (item.showCount !== false) {
                        <span class="ai-help-panel__home-attention-item-count">{{ item.count }}</span>
                      }
                      <span class="ai-help-panel__home-attention-item-label">{{ item.label }}</span>
                    </button>
                  }
                </div>
              </div>
            } @else if (state.isDashboardSummaryLoading()) {
              <div class="ai-help-panel__home-attention" aria-busy="true">
                <div class="ai-help-panel__home-attention-header">
                  <h4 class="ai-help-panel__home-attention-title">Needs Your Attention</h4>
                </div>
                <div class="ai-help-panel__home-attention-loading" role="status" aria-live="polite">
                  <app-spinner [size]="18"></app-spinner>
                  <span>Loading…</span>
                </div>
              </div>
            }

            <div class="ai-help-panel__home-popular">
              <h4 class="ai-help-panel__home-popular-title">Popular actions</h4>
              <div class="ai-help-panel__home-popular-grid">
                @for (qa of popularActions(); track qa.id) {
                  <button
                    type="button"
                    class="ai-help-panel__home-popular-btn"
                    [attr.data-tone]="qa.tone || 'blue'"
                    (click)="onSuggestionClick(qa.prompt)"
                  >
                    <span
                      class="ai-help-panel__home-popular-icon"
                      [attr.data-tone]="qa.tone || 'blue'"
                      aria-hidden="true"
                    >
                      <mat-icon>{{ qa.icon }}</mat-icon>
                    </span>
                    <span class="ai-help-panel__home-popular-label">{{ qa.label }}</span>
                  </button>
                }
              </div>
            </div>

            <div class="ai-help-panel__home-try">
              <div class="ai-help-panel__home-try-header">
                <h4 class="ai-help-panel__home-try-title">{{ tryAsk().title }}</h4>
                <button
                  type="button"
                  class="ai-help-panel__home-try-cta"
                  [attr.aria-expanded]="isSamplePromptsOpen()"
                  [attr.aria-pressed]="isSamplePromptsOpen()"
                  (click)="onToggleSamplePrompts($event)"
                >
                  {{ isSamplePromptsOpen() ? 'See Less' : 'See More' }}
                </button>
              </div>

              <div class="ai-help-panel__home-try-list">
                @for (item of tryAsk().items; track item.id) {
                  <button
                    type="button"
                    class="ai-help-panel__home-try-item"
                    [attr.data-tone]="item.tone || 'indigo'"
                    (click)="onSuggestionClick(item.prompt)"
                  >
                    <span
                      class="ai-help-panel__home-try-item-icon"
                      [attr.data-tone]="item.tone || 'indigo'"
                      aria-hidden="true"
                    >
                      <mat-icon>{{ item.icon }}</mat-icon>
                    </span>
                    <span class="ai-help-panel__home-try-item-label">{{ item.label }}</span>
                    <mat-icon class="ai-help-panel__home-action-chevron" aria-hidden="true"
                      >chevron_right</mat-icon
                    >
                  </button>
                }
              </div>
            </div>
          </section>
        } @else {
          <div class="ai-help-panel__messages">
            @for (message of state.messages(); track message.id; let i = $index) {
              <article
                class="ai-help-panel__message"
                [class.ai-help-panel__message--user]="message.role === 'user'"
                [class.ai-help-panel__message--assistant]="message.role === 'assistant'"
                [class.ai-help-panel__message--error]="message.error"
                [class.ai-help-panel__message--pending]="message.pending"
                [class.ai-help-panel__message--grouped]="!shouldShowMessageAvatar(i)"
              >
                @if (message.role === 'assistant') {
                  @if (shouldShowMessageAvatar(i)) {
                    <div class="ai-help-panel__message-avatar" aria-hidden="true">
                      <img
                        class="ai-help-panel__message-avatar-gif"
                        src="assets/images/timh1.png"
                        alt=""
                        width="40"
                        height="40"
                        decoding="async"
                        draggable="false"
                      />
                    </div>
                  } @else {
                    <div
                      class="ai-help-panel__message-avatar ai-help-panel__message-avatar--spacer"
                      aria-hidden="true"
                    ></div>
                  }
                }
                <div class="ai-help-panel__message-bubble">
                  @if (message.pending) {
                    <div class="ai-help-panel__pending">
                      <app-spinner [size]="20"></app-spinner>
                      <span class="ai-help-panel__thinking">Thinking…</span>
                    </div>
                  } @else {
                    @if (message.isAudio && speechOutput.isSupported()) {
                      <button
                        type="button"
                        class="ai-help-panel__audio-btn"
                        [class.ai-help-panel__audio-btn--playing]="speechOutput.isSpeakingMessage(message.id)"
                        [matTooltip]="messageAudioTooltip(message.id, message.isAudio)"
                        matTooltipAlways
                        [attr.aria-label]="messageAudioTooltip(message.id, message.isAudio)"
                        (click)="onToggleMessageAudio(message.id, message.messageContent)"
                      >
                        <mat-icon>{{ speechOutput.isSpeakingMessage(message.id) ? 'stop_circle' : 'volume_up' }}</mat-icon>
                      </button>
                    }
                    @if (toGuideContent(message.messageContent); as guide) {
                      <div class="ai-help-panel__guide">
                      @if (guide.intro) {
                        <p
                          class="ai-help-panel__guide-intro"
                          [class.ai-help-panel__guide-intro--speaking]="speechOutput.isSpeakingMessage(message.id) && speechOutput.activePart()?.partKind === 'intro'"
                        >
                          {{ guide.intro }}
                        </p>
                      }
                      @if (guide.video) {
                        <div class="ai-help-panel__guide-video">
                          <video
                            class="ai-help-panel__video-player"
                            controls
                            preload="metadata"
                            playsinline
                            [src]="guide.video"
                            aria-label="Guide demonstration video"
                          >
                            Your browser does not support video playback.
                          </video>
                        </div>
                      }
                      @for (method of guide.methods; track $index; let methodIndex = $index) {
                        <section
                          class="ai-help-panel__guide-method"
                          [class.ai-help-panel__guide-method--expandable]="isExpandableGuideMethod(method)"
                        >
                          @if (method.label) {
                            @if (isExpandableGuideMethod(method)) {
                              <button
                                type="button"
                                class="ai-help-panel__guide-method-toggle"
                                [class.ai-help-panel__guide-method-toggle--expanded]="isMethodExpanded(message.id, methodIndex)"
                                [class.ai-help-panel__guide-method-toggle--speaking]="speechOutput.isSpeakingMessage(message.id) && speechOutput.activePart()?.partKind === 'method-label' && speechOutput.activePart()?.methodIndex === methodIndex"
                                [attr.aria-expanded]="isMethodExpanded(message.id, methodIndex)"
                                (click)="toggleMethodExpansion(message.id, methodIndex, message.messageContent)"
                              >
                                <mat-icon
                                  class="ai-help-panel__guide-method-chevron"
                                  aria-hidden="true"
                                >
                                  chevron_right
                                </mat-icon>
                                <span>{{ method.label }}</span>
                              </button>
                            } @else {
                              <h4
                                class="ai-help-panel__guide-method-label"
                                [class.ai-help-panel__guide-method-label--speaking]="speechOutput.isSpeakingMessage(message.id) && speechOutput.activePart()?.partKind === 'method-label' && speechOutput.activePart()?.methodIndex === methodIndex"
                              >
                                {{ method.label }}
                              </h4>
                            }
                          }
                          @if (shouldShowMethodSteps(message.id, guide, methodIndex)) {
                            @if (method.video) {
                              <div class="ai-help-panel__guide-video">
                                <video
                                  class="ai-help-panel__video-player"
                                  controls
                                  preload="metadata"
                                  playsinline
                                  [src]="method.video"
                                  [attr.aria-label]="method.label ? method.label + ' demonstration video' : 'Guide demonstration video'"
                                >
                                  Your browser does not support video playback.
                                </video>
                              </div>
                            }
                            <ol class="ai-help-panel__guide-steps">
                              @for (step of method.steps; track $index; let stepIndex = $index) {
                                <li
                                  class="ai-help-panel__guide-step"
                                  [class.ai-help-panel__guide-step--speaking]="isSpeakingStep(message.id, methodIndex, stepIndex)"
                                >
                                  <span class="ai-help-panel__guide-step-number" aria-hidden="true">{{ stepIndex + 1 }}</span>
                                  <div class="ai-help-panel__guide-step-body">
                                    <span class="ai-help-panel__guide-step-text">{{ step.text }}</span>
                                    @if (step.link; as link) {
                                      @if (isInternalGuideLink(link)) {
                                        <a
                                          class="ai-help-panel__guide-link"
                                          [class.ai-help-panel__guide-link--speaking]="
                                            speechOutput.isSpeakingMessage(message.id) &&
                                            speechOutput.activePart()?.partKind === 'link-cue' &&
                                            speechOutput.activePart()?.methodIndex === methodIndex
                                          "
                                          [routerLink]="getInternalRoute(link.url)"
                                          (click)="onGuideLinkClick()"
                                        >
                                          {{ link.label }}
                                        </a>
                                      } @else {
                                        <a
                                          class="ai-help-panel__guide-link"
                                          [class.ai-help-panel__guide-link--speaking]="
                                            speechOutput.isSpeakingMessage(message.id) &&
                                            speechOutput.activePart()?.partKind === 'link-cue' &&
                                            speechOutput.activePart()?.methodIndex === methodIndex
                                          "
                                          [href]="link.url"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {{ link.label }}
                                        </a>
                                      }
                                    }
                                  </div>
                                </li>
                              }
                            </ol>
                          }
                        </section>
                      }
                      @if (guide.note) {
                        <p
                          class="ai-help-panel__guide-note"
                          [class.ai-help-panel__guide-note--speaking]="speechOutput.isSpeakingMessage(message.id) && speechOutput.activePart()?.partKind === 'note'"
                        >
                          {{ guide.note }}
                        </p>
                      }
                      </div>
                    } @else {
                      @if (toDataContent(message.messageContent); as dataReply) {
                        <div class="ai-help-panel__data">
                          @if (dataReply.intro && dataReply.dataKind !== 'client_brief') {
                            <p class="ai-help-panel__data-intro">{{ dataReply.intro }}</p>
                          }

                          @if (dataReply.dataKind === 'email_preview' && dataReply.emailPreviewContent; as email) {
                            <div class="ai-help-panel__email-preview">
                              <dl class="ai-help-panel__email-meta">
                                <div class="ai-help-panel__email-meta-row">
                                  <dt>To</dt>
                                  <dd>
                                    @for (address of email.to; track address) {
                                      <span class="ai-help-panel__email-chip">{{ address }}</span>
                                    } @empty {
                                      <span class="ai-help-panel__email-empty">No recipients</span>
                                    }
                                  </dd>
                                </div>
                                @if (email.cc.length) {
                                  <div class="ai-help-panel__email-meta-row">
                                    <dt>Cc</dt>
                                    <dd>
                                      @for (address of email.cc; track address) {
                                        <span class="ai-help-panel__email-chip">{{ address }}</span>
                                      }
                                    </dd>
                                  </div>
                                }
                                <div class="ai-help-panel__email-meta-row">
                                  <dt>Subject</dt>
                                  <dd class="ai-help-panel__email-subject">{{ email.subject || '(No subject)' }}</dd>
                                </div>
                              </dl>
                              <iframe
                                class="ai-help-panel__email-frame"
                                title="Email preview"
                                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                                [srcdoc]="emailPreviewDoc(email)"
                                (load)="onEmailPreviewLoad($event)"
                              ></iframe>
                            </div>
                          } @else if (dataReply.dataKind === 'project_list' && dataReply.projects?.length) {
                            <ul class="ai-help-panel__data-list">
                              @for (project of dataReply.projects; track project.projectId || project.projectName + '-' + $index) {
                                <li>
                                  <button
                                    type="button"
                                    class="ai-help-panel__data-card"
                                    [disabled]="!isDataSelectionEnabled(message.id)"
                                    (click)="onDataProjectClick(project)"
                                  >
                                    <span class="ai-help-panel__data-card-title">{{ project.projectName }}</span>
                                    <span class="ai-help-panel__data-card-meta">
                                      @if (project.status) {
                                        <span class="ai-help-panel__data-card-status">{{ project.status }}</span>
                                      }
                                      @if (project.daysOverdue != null) {
                                        <span class="ai-help-panel__data-card-overdue">{{ formatProjectDaysOverdue(project.daysOverdue) }}</span>
                                      }
                                      @if (project.overdueCount != null) {
                                        <span class="ai-help-panel__data-card-overdue">{{ formatProjectOverdueCount(project.overdueCount) }}</span>
                                      }
                                      @if (project.dueDate) {
                                        <span class="ai-help-panel__data-card-due">Due {{ formatDailyHoursDateLabel(project.dueDate) }}</span>
                                      }
                                      @if (project.projectedCompletion) {
                                        <span class="ai-help-panel__data-card-due">Projected {{ formatDailyHoursDateLabel(project.projectedCompletion) }}</span>
                                      }
                                      @if (project.percentComplete != null) {
                                        <span class="ai-help-panel__data-card-progress">{{ formatProjectPercent(project.percentComplete) }}</span>
                                      }
                                      @if (project.owner) {
                                        <span class="ai-help-panel__data-card-owner">{{ project.owner }}</span>
                                      }
                                    </span>
                                    @if (project.percentComplete != null) {
                                      <div
                                        class="ai-help-panel__data-progress-track"
                                        role="progressbar"
                                        [attr.aria-valuenow]="projectProgressWidth(project.percentComplete)"
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                        [attr.aria-label]="project.projectName + ' progress'"
                                      >
                                        <div
                                          class="ai-help-panel__data-progress-fill"
                                          [style.width.%]="projectProgressWidth(project.percentComplete)"
                                        ></div>
                                      </div>
                                    }
                                  </button>
                                </li>
                              }
                            </ul>
                          } @else if (dataReply.blocks?.length) {
                            @if (asBlocksCountClientMetric(dataReply.blocks, dataReply.data); as metric) {
                              <div class="ai-help-panel__data-blocks ai-help-panel__data-blocks--metric">
                                <div class="ai-help-panel__data-metric" aria-label="Count">
                                  <div class="ai-help-panel__data-metric-copy">
                                    <span class="ai-help-panel__data-metric-value">{{ metric.count }}</span>
                                    <span class="ai-help-panel__data-metric-label">{{ metric.label }}</span>
                                  </div>
                                  <span class="ai-help-panel__data-metric-icon" aria-hidden="true">
                                    <mat-icon>folder_open</mat-icon>
                                  </span>
                                </div>
                                @if (metric.client) {
                                  <div class="ai-help-panel__data-metric-footer">
                                    <span class="ai-help-panel__data-metric-footer-label">Client</span>
                                    <span class="ai-help-panel__data-client-chip">
                                      <span class="ai-help-panel__data-client-chip-avatar" aria-hidden="true">
                                        {{ clientInitial(metric.client) }}
                                      </span>
                                      <span class="ai-help-panel__data-client-chip-name">{{ metric.client }}</span>
                                    </span>
                                  </div>
                                }
                              </div>
                            } @else {
                            <div class="ai-help-panel__data-blocks">
                              @for (block of dataReply.blocks; track $index) {
                                @if (block.type === 'stat_cards') {
                                  <section class="ai-help-panel__data-stat-cards" aria-label="Summary">
                                    @if (block.title) {
                                      <h4 class="ai-help-panel__data-block-title">{{ block.title }}</h4>
                                    }
                                    <div class="ai-help-panel__data-stat-cards-grid">
                                      @for (item of block.items; track item.label + '-' + $index) {
                                        <article
                                          class="ai-help-panel__data-stat-card"
                                          [attr.data-tone]="item.tone"
                                        >
                                          <span class="ai-help-panel__data-stat-card-value">{{ formatDataBlockStatValue(item.value) }}</span>
                                          <span class="ai-help-panel__data-stat-card-label">{{ item.label }}</span>
                                        </article>
                                      }
                                    </div>
                                  </section>
                                } @else if (block.type === 'item_list') {
                                  <section class="ai-help-panel__data-item-list" aria-label="Items">
                                    @if (block.title) {
                                      <h4 class="ai-help-panel__data-block-title">{{ block.title }}</h4>
                                    }
                                    <ul class="ai-help-panel__data-item-list-rows">
                                      @for (item of block.items; track item.title + '-' + $index) {
                                        <li
                                          class="ai-help-panel__data-item-list-row"
                                          [class.ai-help-panel__data-item-list-row--nested]="itemListRowHasDetails(item)"
                                        >
                                          <div class="ai-help-panel__data-item-list-head">
                                            <span class="ai-help-panel__data-item-list-title">
                                              @if (item.link) {
                                                @if (getInternalRoute(item.link); as route) {
                                                  <a
                                                    class="ai-help-panel__data-project-task-link"
                                                    [routerLink]="route"
                                                    (click)="onGuideLinkClick()"
                                                  >
                                                    {{ item.title }}
                                                  </a>
                                                } @else {
                                                  <a
                                                    class="ai-help-panel__data-project-task-link"
                                                    [href]="item.link"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    {{ item.title }}
                                                  </a>
                                                }
                                              } @else {
                                                {{ item.title }}
                                              }
                                            </span>
                                            @if (itemListHasHoursFields(item)) {
                                              @if (itemListSubmittedTone(item.submitted); as submittedTone) {
                                                <span
                                                  class="ai-help-panel__data-item-list-meta"
                                                  [attr.data-tone]="submittedTone"
                                                >
                                                  <span class="ai-help-panel__data-item-list-meta-label">
                                                    {{ item.submitted ? 'Submitted' : 'Not submitted' }}
                                                  </span>
                                                </span>
                                              }
                                            } @else {
                                              @if (itemListTicketCountMeta(item); as ticketCountMeta) {
                                                <span class="ai-help-panel__data-item-list-meta">
                                                  <span class="ai-help-panel__data-item-list-meta-label">{{ ticketCountMeta }}</span>
                                                </span>
                                              } @else {
                                                @if (itemListDelayMeta(item); as delayMeta) {
                                                  <span
                                                    class="ai-help-panel__data-item-list-meta"
                                                    data-tone="red"
                                                  >
                                                    <span class="ai-help-panel__data-item-list-meta-label">{{ delayMeta }}</span>
                                                  </span>
                                                } @else if (item.status) {
                                                  @if (itemListMetaParts(item.status); as metaParts) {
                                                    <span
                                                      class="ai-help-panel__data-item-list-meta"
                                                      [attr.data-tone]="itemListMetaTone(metaParts.label)"
                                                    >
                                                      <span class="ai-help-panel__data-item-list-meta-label">{{ metaParts.label }}</span>
                                                      @if (metaParts.detail) {
                                                        <span class="ai-help-panel__data-item-list-meta-detail">{{ metaParts.detail }}</span>
                                                      }
                                                    </span>
                                                  }
                                                } @else if (item.priority) {
                                                  @if (itemListMetaParts(item.priority); as metaParts) {
                                                    <span
                                                      class="ai-help-panel__data-item-list-meta"
                                                      [attr.data-tone]="itemListMetaTone(metaParts.label)"
                                                    >
                                                      <span class="ai-help-panel__data-item-list-meta-label">{{ metaParts.label }}</span>
                                                      @if (metaParts.detail) {
                                                        <span class="ai-help-panel__data-item-list-meta-detail">{{ metaParts.detail }}</span>
                                                      }
                                                    </span>
                                                  }
                                                }
                                              }
                                            }
                                          </div>
                                          @if (item.assignees) {
                                            <span class="ai-help-panel__data-item-list-assignees">
                                              {{ item.assignees }}
                                            </span>
                                          }
                                          @if (itemListHasHoursFields(item)) {
                                            <div class="ai-help-panel__data-item-list-hours" aria-label="Hours">
                                              @if (item.totalHours != null) {
                                                <span class="ai-help-panel__data-item-list-hour">
                                                  <span class="ai-help-panel__data-item-list-hour-label">Total</span>
                                                  <span class="ai-help-panel__data-item-list-hour-value">
                                                    {{ formatRankingHours(item.totalHours) }}
                                                  </span>
                                                </span>
                                              }
                                              @if (item.billableHours != null) {
                                                <span class="ai-help-panel__data-item-list-hour">
                                                  <span class="ai-help-panel__data-item-list-hour-label">Billable</span>
                                                  <span class="ai-help-panel__data-item-list-hour-value">
                                                    {{ formatRankingHours(item.billableHours) }}
                                                  </span>
                                                </span>
                                              }
                                              @if (item.aiHours != null) {
                                                <span class="ai-help-panel__data-item-list-hour">
                                                  <span class="ai-help-panel__data-item-list-hour-label">AI</span>
                                                  <span class="ai-help-panel__data-item-list-hour-value">
                                                    {{ formatRankingHours(item.aiHours) }}
                                                  </span>
                                                </span>
                                              }
                                            </div>
                                          } @else {
                                            @if (itemListDelayPlotPercent(item, block.items); as plotPercent) {
                                              <div class="ai-help-panel__data-ranking-plot">
                                                <div
                                                  class="ai-help-panel__data-progress-track"
                                                  role="progressbar"
                                                  data-tone="red"
                                                  [attr.aria-valuenow]="projectProgressWidth(plotPercent)"
                                                  aria-valuemin="0"
                                                  aria-valuemax="100"
                                                  [attr.aria-label]="item.title + ' delay ' + formatProjectPercent(plotPercent)"
                                                >
                                                  <div
                                                    class="ai-help-panel__data-progress-fill"
                                                    [style.width.%]="projectProgressWidth(plotPercent)"
                                                  ></div>
                                                </div>
                                                <span
                                                  class="ai-help-panel__data-ranking-percent"
                                                  data-tone="red"
                                                >
                                                  {{ formatProjectPercent(plotPercent) }}
                                                </span>
                                              </div>
                                            }
                                          }
                                          @if (itemListDelayDateRange(item); as dateRange) {
                                            <span class="ai-help-panel__data-item-list-delay-dates">{{ dateRange }}</span>
                                          }
                                          @if (item.tickets?.length) {
                                            <ul class="ai-help-panel__data-item-list-tickets" aria-label="Tickets">
                                              @for (ticket of item.tickets; track ticket.ticket + '-' + $index) {
                                                <li class="ai-help-panel__data-item-list-ticket">
                                                  <div class="ai-help-panel__data-item-list-ticket-head">
                                                    @if (itemListTicketTypeLabel(ticket); as ticketType) {
                                                      <span class="ai-help-panel__data-item-list-ticket-type">{{ ticketType }}</span>
                                                    }
                                                    <span class="ai-help-panel__data-item-list-ticket-title">
                                                      @if (ticket.link) {
                                                        @if (getInternalRoute(ticket.link); as route) {
                                                          <a
                                                            class="ai-help-panel__data-project-task-link"
                                                            [routerLink]="route"
                                                            (click)="onGuideLinkClick()"
                                                          >
                                                            {{ ticket.ticket }}
                                                          </a>
                                                        } @else {
                                                          <a
                                                            class="ai-help-panel__data-project-task-link"
                                                            [href]="ticket.link"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                          >
                                                            {{ ticket.ticket }}
                                                          </a>
                                                        }
                                                      } @else {
                                                        {{ ticket.ticket }}
                                                      }
                                                    </span>
                                                  </div>
                                                  @if (ticket.project) {
                                                    <span class="ai-help-panel__data-item-list-ticket-project">{{ ticket.project }}</span>
                                                  }
                                                  @if (ticket.reason) {
                                                    <p class="ai-help-panel__data-item-list-ticket-reason">{{ ticket.reason }}</p>
                                                  }
                                                </li>
                                              }
                                            </ul>
                                          }
                                          @if (item.overProjects?.length) {
                                            <ul class="ai-help-panel__data-item-list-projects">
                                              @for (project of item.overProjects; track project.project + '-' + $index) {
                                                <li class="ai-help-panel__data-item-list-project">
                                                  <div class="ai-help-panel__data-item-list-project-row">
                                                    <span class="ai-help-panel__data-item-list-project-name">{{ project.project }}</span>
                                                    @if (itemListOverProjectHours(project); as hoursLabel) {
                                                      <span class="ai-help-panel__data-item-list-project-hours">{{ hoursLabel }}</span>
                                                    }
                                                  </div>
                                                  @if (itemListOverProjectPlotPercent(project); as plotPercent) {
                                                    <div class="ai-help-panel__data-ranking-plot">
                                                      <div
                                                        class="ai-help-panel__data-progress-track"
                                                        role="progressbar"
                                                        [attr.data-tone]="itemListOverProjectTone(plotPercent)"
                                                        [attr.aria-valuenow]="projectProgressWidth(plotPercent)"
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                        [attr.aria-label]="project.project + ' utilization ' + formatProjectPercent(plotPercent)"
                                                      >
                                                        <div
                                                          class="ai-help-panel__data-progress-fill"
                                                          [style.width.%]="projectProgressWidth(plotPercent)"
                                                        ></div>
                                                      </div>
                                                      <span
                                                        class="ai-help-panel__data-ranking-percent"
                                                        [attr.data-tone]="itemListOverProjectTone(plotPercent)"
                                                      >
                                                        {{ formatProjectPercent(plotPercent) }}
                                                      </span>
                                                    </div>
                                                  }
                                                  @if (itemListOverProjectMeta(project); as overageLabel) {
                                                    <span class="ai-help-panel__data-item-list-project-meta">{{ overageLabel }}</span>
                                                  }
                                                </li>
                                              }
                                            </ul>
                                          }
                                        </li>
                                      }
                                    </ul>
                                  </section>
                                } @else if (block.type === 'text') {
                                  <section class="ai-help-panel__data-text">
                                    @for (segment of block.segments; track $index) {
                                      @switch (segment.kind) {
                                        @case ('heading') {
                                          <h4 class="ai-help-panel__data-text-heading">
                                            @for (run of segment.runs; track $index) {
                                              @if (run.bold) {
                                                <strong>{{ run.text }}</strong>
                                              } @else {
                                                {{ run.text }}
                                              }
                                            }
                                          </h4>
                                        }
                                        @case ('paragraph') {
                                          <p class="ai-help-panel__data-text-paragraph">
                                            @for (run of segment.runs; track $index) {
                                              @if (run.bold) {
                                                <strong>{{ run.text }}</strong>
                                              } @else {
                                                {{ run.text }}
                                              }
                                            }
                                          </p>
                                        }
                                        @case ('bullets') {
                                          <ul class="ai-help-panel__data-text-list">
                                            @for (item of segment.items; track $index) {
                                              <li>
                                                @for (run of item; track $index) {
                                                  @if (run.bold) {
                                                    <strong>{{ run.text }}</strong>
                                                  } @else {
                                                    {{ run.text }}
                                                  }
                                                }
                                              </li>
                                            }
                                          </ul>
                                        }
                                        @case ('numbered') {
                                          <ol class="ai-help-panel__data-text-list ai-help-panel__data-text-list--numbered">
                                            @for (item of segment.items; track $index) {
                                              <li>
                                                @for (run of item; track $index) {
                                                  @if (run.bold) {
                                                    <strong>{{ run.text }}</strong>
                                                  } @else {
                                                    {{ run.text }}
                                                  }
                                                }
                                              </li>
                                            }
                                          </ol>
                                        }
                                      }
                                    }
                                  </section>
                                } @else if (block.type === 'ranking' && block.items.length) {
                                  <section class="ai-help-panel__data-ranking" aria-label="Ranking">
                                    @if (block.title) {
                                      <h4 class="ai-help-panel__data-block-title">{{ block.title }}</h4>
                                    }
                                    @if (blocksRankingScope(dataReply.blocks); as scope) {
                                      <div class="ai-help-panel__data-ranking-summary">
                                        <span class="ai-help-panel__data-ranking-scope">{{ scope }}</span>
                                      </div>
                                    }
                                    <ol class="ai-help-panel__data-ranking-list">
                                      @for (entry of block.items; track entry.name + '-' + $index; let rank = $index) {
                                        <li>
                                          <div class="ai-help-panel__data-ranking-item">
                                            <span class="ai-help-panel__data-ranking-place" aria-hidden="true">{{ rank + 1 }}</span>
                                            <div class="ai-help-panel__data-ranking-body">
                                              <div class="ai-help-panel__data-ranking-row">
                                                <span class="ai-help-panel__data-ranking-name">
                                                  {{ entry.name }}
                                                  @if (entry.type) {
                                                    <span class="ai-help-panel__data-ranking-task-type">{{ entry.type }}</span>
                                                  }
                                                </span>
                                                <span class="ai-help-panel__data-ranking-hours">
                                                  {{ formatRankingEntryValue(entry, blocksRankingItemLabel(dataReply.blocks)) }}
                                                </span>
                                              </div>
                                              @if (rankingPlotPercent(entry); as plotPercent) {
                                                <div class="ai-help-panel__data-ranking-plot">
                                                  <div
                                                    class="ai-help-panel__data-progress-track"
                                                    role="progressbar"
                                                    [attr.aria-valuenow]="projectProgressWidth(plotPercent)"
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                    [attr.aria-label]="entry.name + ' share ' + formatProjectPercent(plotPercent)"
                                                  >
                                                    <div
                                                      class="ai-help-panel__data-progress-fill"
                                                      [style.width.%]="projectProgressWidth(plotPercent)"
                                                    ></div>
                                                  </div>
                                                  <span class="ai-help-panel__data-ranking-percent">
                                                    {{ formatProjectPercent(plotPercent) }}
                                                  </span>
                                                </div>
                                              }
                                              @if (entry.byTask?.length) {
                                                <ul class="ai-help-panel__data-ranking-tasks">
                                                  @for (task of entry.byTask; track task.item + '-' + $index) {
                                                    <li class="ai-help-panel__data-ranking-task">
                                                      <div class="ai-help-panel__data-ranking-task-main">
                                                        <span class="ai-help-panel__data-ranking-task-title">{{ task.item }}</span>
                                                        @if (task.type) {
                                                          <span class="ai-help-panel__data-ranking-task-type">{{ task.type }}</span>
                                                        }
                                                      </div>
                                                      <span class="ai-help-panel__data-ranking-task-hours">
                                                        {{ formatRankingHours(task.hours) }}
                                                      </span>
                                                    </li>
                                                  }
                                                </ul>
                                              }
                                            </div>
                                          </div>
                                        </li>
                                      }
                                    </ol>
                                  </section>
                                } @else if (block.type === 'key_values') {
                                  @if (visibleKeyValueRows(block, dataReply.blocks); as rows) {
                                    @if (rows.length) {
                                      <section class="ai-help-panel__data-key-values" aria-label="Details">
                                        @if (block.title) {
                                          <h4 class="ai-help-panel__data-block-title">{{ block.title }}</h4>
                                        }
                                        <dl class="ai-help-panel__data-fields">
                                          @for (row of rows; track row.label + '-' + $index) {
                                            <div class="ai-help-panel__data-field">
                                              <dt>{{ formatKeyValueLabel(row.label) }}</dt>
                                              <dd>
                                                @if (
                                                  isKeyValueProjectNameRow(row.label) &&
                                                  keyValuesProjectRoute(block); as route
                                                ) {
                                                  <a
                                                    class="ai-help-panel__data-project-task-link"
                                                    [routerLink]="route"
                                                    (click)="onGuideLinkClick()"
                                                  >
                                                    {{ formatKeyValueDisplay(row.label, row.value) }}
                                                  </a>
                                                } @else {
                                                  @if (keyValueRowLinkRoute(row, block); as kvRoute) {
                                                    <a
                                                      class="ai-help-panel__data-project-task-link"
                                                      [routerLink]="kvRoute"
                                                      (click)="onGuideLinkClick()"
                                                    >
                                                      {{ keyValueLinkDisplay(row.label, row.value) }}
                                                    </a>
                                                  } @else {
                                                    @if (keyValueRowLinkHref(row, block); as kvHref) {
                                                      <a
                                                        class="ai-help-panel__data-project-task-link"
                                                        [href]="kvHref"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                      >
                                                        {{ keyValueLinkDisplay(row.label, row.value) }}
                                                      </a>
                                                    } @else {
                                                      {{ formatKeyValueDisplay(row.label, row.value) }}
                                                    }
                                                  }
                                                }
                                              </dd>
                                            </div>
                                          }
                                        </dl>
                                      </section>
                                    }
                                  }
                                } @else if (block.type === 'table' && block.columns.length && block.rows.length) {
                                  <section class="ai-help-panel__data-table" aria-label="Table">
                                    @if (block.title) {
                                      <h4 class="ai-help-panel__data-block-title">{{ block.title }}</h4>
                                    }
                                    <div class="ai-help-panel__data-table-scroll">
                                      <table class="ai-help-panel__data-table-grid">
                                        <thead>
                                          <tr>
                                            @for (col of block.columns; track col.prop + '-' + $index) {
                                              <th scope="col">{{ col.fieldName }}</th>
                                            }
                                          </tr>
                                        </thead>
                                        <tbody>
                                          @for (row of block.rows; track $index) {
                                            <tr>
                                              @for (col of block.columns; track col.prop + '-' + $index) {
                                                <td>{{ tableCellValue(row, col.prop) }}</td>
                                              }
                                            </tr>
                                          }
                                        </tbody>
                                      </table>
                                    </div>
                                  </section>
                                } @else if (block.type === 'section') {
                                  <section class="ai-help-panel__data-section" aria-label="Section">
                                    @if (block.heading) {
                                      <h4 class="ai-help-panel__data-text-heading">{{ block.heading }}</h4>
                                    }
                                    @for (segment of block.segments; track $index) {
                                      @switch (segment.kind) {
                                        @case ('heading') {
                                          <h4 class="ai-help-panel__data-text-heading">
                                            @for (run of segment.runs; track $index) {
                                              @if (run.bold) {
                                                <strong>{{ run.text }}</strong>
                                              } @else {
                                                {{ run.text }}
                                              }
                                            }
                                          </h4>
                                        }
                                        @case ('paragraph') {
                                          <p class="ai-help-panel__data-text-paragraph">
                                            @for (run of segment.runs; track $index) {
                                              @if (run.bold) {
                                                <strong>{{ run.text }}</strong>
                                              } @else {
                                                {{ run.text }}
                                              }
                                            }
                                          </p>
                                        }
                                        @case ('bullets') {
                                          <ul class="ai-help-panel__data-text-list">
                                            @for (item of segment.items; track $index) {
                                              <li>
                                                @for (run of item; track $index) {
                                                  @if (run.bold) {
                                                    <strong>{{ run.text }}</strong>
                                                  } @else {
                                                    {{ run.text }}
                                                  }
                                                }
                                              </li>
                                            }
                                          </ul>
                                        }
                                        @case ('numbered') {
                                          <ol class="ai-help-panel__data-text-list ai-help-panel__data-text-list--numbered">
                                            @for (item of segment.items; track $index) {
                                              <li>
                                                @for (run of item; track $index) {
                                                  @if (run.bold) {
                                                    <strong>{{ run.text }}</strong>
                                                  } @else {
                                                    {{ run.text }}
                                                  }
                                                }
                                              </li>
                                            }
                                          </ol>
                                        }
                                      }
                                    }
                                  </section>
                                }
                              }
                            </div>
                            }
                          } @else if (dataReply.dataKind === 'projects_over_estimate' && dataReply.projectsOverEstimateContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (
                                dataReply.projectsOverEstimateContent.count != null ||
                                dataReply.projectsOverEstimateContent.overPercent != null
                              ) {
                                <div class="ai-help-panel__data-ranking-summary">
                                  @if (dataReply.projectsOverEstimateContent.count != null) {
                                    <span class="ai-help-panel__data-ranking-count">
                                      Count: {{ dataReply.projectsOverEstimateContent.count }}
                                    </span>
                                  }
                                  @if (dataReply.projectsOverEstimateContent.overPercent != null) {
                                    <span class="ai-help-panel__data-ranking-scope">
                                      Over {{ formatProjectPercent(dataReply.projectsOverEstimateContent.overPercent) }}
                                    </span>
                                  }
                                </div>
                              }

                              @if (dataReply.projectsOverEstimateContent.matches.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (project of dataReply.projectsOverEstimateContent.matches; track project.projectName + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Project</dt>
                                          <dd>{{ project.projectName }}</dd>
                                        </div>
                                        @if (project.owner) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Owner</dt>
                                            <dd>{{ project.owner }}</dd>
                                          </div>
                                        }
                                        @if (project.estimatedHours != null) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Estimated</dt>
                                            <dd>{{ formatRankingHours(project.estimatedHours) }}</dd>
                                          </div>
                                        }
                                        @if (project.actualHours != null) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Actual</dt>
                                            <dd>{{ formatRankingHours(project.actualHours) }}</dd>
                                          </div>
                                        }
                                        @if (project.overrunPercent != null) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Overrun</dt>
                                            <dd>
                                              <span class="ai-help-panel__data-card-overdue">{{ formatProjectPercent(project.overrunPercent) }}</span>
                                            </dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.projectsOverEstimateContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of projects.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'project_health' && dataReply.projectHealthContent) {
                            <div class="ai-help-panel__data-health">
                              <span class="ai-help-panel__data-health-title">{{ dataReply.projectHealthContent.projectName }}</span>
                              <span class="ai-help-panel__data-health-meta">
                                @if (dataReply.projectHealthContent.status) {
                                  <span class="ai-help-panel__data-health-status">{{ dataReply.projectHealthContent.status }}</span>
                                }
                                @if (shouldShowHealthDaysRemaining(dataReply.projectHealthContent)) {
                                  <span class="ai-help-panel__data-health-days">{{ formatProjectDaysRemaining(dataReply.projectHealthContent.daysRemaining) }}</span>
                                }
                                @if (dataReply.projectHealthContent.dueDate) {
                                  <span class="ai-help-panel__data-health-due">Due {{ formatDailyHoursDateLabel(dataReply.projectHealthContent.dueDate) }}</span>
                                }
                                @if (
                                  dataReply.projectHealthContent.originalDueDate &&
                                  dataReply.projectHealthContent.originalDueDate !== dataReply.projectHealthContent.dueDate
                                ) {
                                  <span class="ai-help-panel__data-health-due">Original {{ formatDailyHoursDateLabel(dataReply.projectHealthContent.originalDueDate) }}</span>
                                }
                                @if (dataReply.projectHealthContent.owner) {
                                  <span class="ai-help-panel__data-health-owner">{{ dataReply.projectHealthContent.owner }}</span>
                                }
                                @if (dataReply.projectHealthContent.openTaskCount != null) {
                                  <span class="ai-help-panel__data-health-tasks">{{ dataReply.projectHealthContent.openTaskCount }} open tasks</span>
                                }
                              </span>
                              @if (dataReply.projectHealthContent.percentComplete != null) {
                                <div class="ai-help-panel__data-health-progress">
                                  <span class="ai-help-panel__data-card-progress">{{ formatProjectPercent(dataReply.projectHealthContent.percentComplete) }}</span>
                                  <div
                                    class="ai-help-panel__data-progress-track"
                                    role="progressbar"
                                    [attr.aria-valuenow]="projectProgressWidth(dataReply.projectHealthContent.percentComplete)"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    [attr.aria-label]="dataReply.projectHealthContent.projectName + ' progress'"
                                  >
                                    <div
                                      class="ai-help-panel__data-progress-fill"
                                      [style.width.%]="projectProgressWidth(dataReply.projectHealthContent.percentComplete)"
                                    ></div>
                                  </div>
                                </div>
                              }
                              @if (dataReply.projectHealthContent.hours; as hours) {
                                <div class="ai-help-panel__data-health-hours" aria-label="Hours">
                                  @if (hours.totalHours != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Total</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ formatRankingHours(hours.totalHours) }}</span>
                                    </div>
                                  }
                                  @if (hours.loggedHours != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Logged</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ formatRankingHours(hours.loggedHours) }}</span>
                                    </div>
                                  }
                                  @if (hours.pendingHours != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Pending</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ formatRankingHours(hours.pendingHours) }}</span>
                                    </div>
                                  }
                                </div>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'project_task_breakdown' && dataReply.projectTaskBreakdownContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (dataReply.projectTaskBreakdownContent.projectName) {
                                <div class="ai-help-panel__data-project-tasks-summary">
                                  <span class="ai-help-panel__data-project-tasks-label">Project</span>
                                  <span class="ai-help-panel__data-project-tasks-name">{{ dataReply.projectTaskBreakdownContent.projectName }}</span>
                                </div>
                              }

                              @if (
                                dataReply.projectTaskBreakdownContent.totalTasks != null ||
                                dataReply.projectTaskBreakdownContent.completedTasks != null ||
                                dataReply.projectTaskBreakdownContent.remainingTasks != null
                              ) {
                                <div class="ai-help-panel__data-health-hours" aria-label="Task counts">
                                  @if (dataReply.projectTaskBreakdownContent.totalTasks != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Total</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.projectTaskBreakdownContent.totalTasks }}</span>
                                    </div>
                                  }
                                  @if (dataReply.projectTaskBreakdownContent.completedTasks != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Completed</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.projectTaskBreakdownContent.completedTasks }}</span>
                                    </div>
                                  }
                                  @if (dataReply.projectTaskBreakdownContent.remainingTasks != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Remaining</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.projectTaskBreakdownContent.remainingTasks }}</span>
                                    </div>
                                  }
                                </div>
                              }

                              @if (dataReply.projectTaskBreakdownContent.remaining.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (task of dataReply.projectTaskBreakdownContent.remaining; track task.taskName + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Task name</dt>
                                          <dd>{{ task.taskName }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ task.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Percent complete</dt>
                                          <dd>
                                            @if (task.percentComplete != null) {
                                              {{ formatProjectPercent(task.percentComplete) }}
                                            } @else {
                                              —
                                            }
                                          </dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Assignees</dt>
                                          <dd>{{ task.assignees || '—' }}</dd>
                                        </div>
                                        @if (task.percentComplete != null) {
                                          <div
                                            class="ai-help-panel__data-progress-track"
                                            role="progressbar"
                                            [attr.aria-valuenow]="projectProgressWidth(task.percentComplete)"
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                            [attr.aria-label]="task.taskName + ' progress'"
                                          >
                                            <div
                                              class="ai-help-panel__data-progress-fill"
                                              [style.width.%]="projectProgressWidth(task.percentComplete)"
                                            ></div>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.projectTaskBreakdownContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of remaining tasks.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'project_tasks' && dataReply.projectTasksContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (dataReply.projectTasksContent.projectName) {
                                <div class="ai-help-panel__data-project-tasks-summary">
                                  <span class="ai-help-panel__data-project-tasks-label">Project</span>
                                  <span class="ai-help-panel__data-project-tasks-name">{{ dataReply.projectTasksContent.projectName }}</span>
                                </div>
                              }

                              @if (dataReply.projectTasksContent.tasks.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (task of dataReply.projectTasksContent.tasks; track task.taskName + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Task name</dt>
                                          <dd>{{ task.taskName }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ task.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Due date</dt>
                                          <dd>{{ task.due ? formatDailyHoursDateLabel(task.due) : '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Assignees</dt>
                                          <dd>{{ task.assignees || '—' }}</dd>
                                        </div>
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'completed_items' && dataReply.completedItemsContent) {
                            <div class="ai-help-panel__data-project-tasks">


                              @for (group of dataReply.completedItemsContent.groups; track group.type) {
                                <div class="ai-help-panel__data-completed-group">
                                  <span class="ai-help-panel__data-project-tasks-label">
                                    {{ formatDataFieldLabel(group.type) }} ({{ group.items.length }})
                                  </span>
                                  <ul class="ai-help-panel__data-project-tasks-list">
                                    @for (item of group.items; track item.name + '-' + $index) {
                                      <li>
                                        <dl class="ai-help-panel__data-project-task">
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Name</dt>
                                            <dd>{{ item.name }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Status</dt>
                                            <dd>{{ item.status || '—' }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Completed on</dt>
                                            <dd>{{ item.completedOn ? formatDailyHoursDateLabel(item.completedOn) : '—' }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Assignees</dt>
                                            <dd>{{ item.assignees || '—' }}</dd>
                                          </div>
                                        </dl>
                                      </li>
                                    }
                                  </ul>
                                </div>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'current_work' && dataReply.currentWorkContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @for (group of dataReply.currentWorkContent.groups; track group.type) {
                                <div class="ai-help-panel__data-completed-group">
                                  <span class="ai-help-panel__data-project-tasks-label">
                                    {{ formatDataFieldLabel(group.type) }} ({{ group.items.length }})
                                  </span>
                                  <ul class="ai-help-panel__data-project-tasks-list">
                                    @for (item of group.items; track item.name + '-' + $index) {
                                      <li>
                                        <dl class="ai-help-panel__data-project-task">
                                          <div class="ai-help-panel__data-project-task-row">

                                            <dd>{{ item.name }}</dd>
                                          </div>
                                        </dl>
                                      </li>
                                    }
                                  </ul>
                                </div>
                              }

                              @if (dataReply.currentWorkContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of current work.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'overdue_tasks' && dataReply.overdueTasksContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (
                                dataReply.overdueTasksContent.count != null ||
                                dataReply.overdueTasksContent.shown != null ||
                                dataReply.overdueTasksContent.scopeLabel ||
                                dataReply.overdueTasksContent.onDate
                              ) {
                                <div class="ai-help-panel__data-comments-summary">
                                  @if (dataReply.overdueTasksContent.scopeLabel) {
                                    <span class="ai-help-panel__data-comments-count">
                                      {{ dataReply.overdueTasksContent.scopeLabel }}
                                    </span>
                                  }
                                  @if (dataReply.overdueTasksContent.count != null) {
                                    <span class="ai-help-panel__data-comments-count">
                                      Count: {{ dataReply.overdueTasksContent.count }}
                                    </span>
                                  }
                                  @if (dataReply.overdueTasksContent.shown != null) {
                                    <span class="ai-help-panel__data-comments-count">
                                      Showing: {{ dataReply.overdueTasksContent.shown }}
                                    </span>
                                  }
                                  @if (dataReply.overdueTasksContent.onDate) {
                                    <span class="ai-help-panel__data-comments-count">
                                      As of {{ formatDailyHoursDateLabel(dataReply.overdueTasksContent.onDate) }}
                                    </span>
                                  }
                                </div>
                              }

                              @if (dataReply.overdueTasksContent.tasks.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (task of dataReply.overdueTasksContent.tasks; track task.taskName + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Task name</dt>
                                          <dd>
                                            @if (task.link) {
                                              @if (getInternalRoute(task.link); as route) {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [routerLink]="route"
                                                  (click)="onGuideLinkClick()"
                                                >
                                                  {{ task.taskName }}
                                                </a>
                                              } @else {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [href]="task.link"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {{ task.taskName }}
                                                </a>
                                              }
                                            } @else {
                                              {{ task.taskName }}
                                            }
                                          </dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ task.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Days overdue</dt>
                                          <dd>
                                            @if (task.daysOverdue != null) {
                                              <span class="ai-help-panel__data-card-overdue">{{ formatProjectDaysOverdue(task.daysOverdue) }}</span>
                                            } @else {
                                              —
                                            }
                                          </dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Due date</dt>
                                          <dd>{{ task.dueDate ? formatDailyHoursDateLabel(task.dueDate) : '—' }}</dd>
                                        </div>
                                        @if (task.project) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Project</dt>
                                            <dd>{{ task.project }}</dd>
                                          </div>
                                        }
                                        @if (task.assignees) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Assignees</dt>
                                            <dd>{{ task.assignees }}</dd>
                                          </div>
                                        }
                                        @if (task.plannedHours != null) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Planned hours</dt>
                                            <dd>{{ formatRankingHours(task.plannedHours) }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.overdueTasksContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of overdue tasks.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'overdue_reqs_issues' && dataReply.overdueReqsIssuesContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (
                                dataReply.overdueReqsIssuesContent.itemType ||
                                dataReply.overdueReqsIssuesContent.scopeLabel ||
                                dataReply.overdueReqsIssuesContent.count != null
                              ) {
                                <div class="ai-help-panel__data-comments-summary">
                                  @if (dataReply.overdueReqsIssuesContent.itemType) {
                                    <span class="ai-help-panel__data-comments-count">
                                      {{ formatDataFieldLabel(dataReply.overdueReqsIssuesContent.itemType) }}
                                    </span>
                                  }
                                  @if (dataReply.overdueReqsIssuesContent.scopeLabel) {
                                    <span class="ai-help-panel__data-comments-count">
                                      {{ dataReply.overdueReqsIssuesContent.scopeLabel }}
                                    </span>
                                  }
                                  @if (dataReply.overdueReqsIssuesContent.count != null) {
                                    <span class="ai-help-panel__data-comments-count">
                                      Count: {{ dataReply.overdueReqsIssuesContent.count }}
                                    </span>
                                  }
                                </div>
                              }

                              @if (dataReply.overdueReqsIssuesContent.items.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (item of dataReply.overdueReqsIssuesContent.items; track item.name + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>
                                            {{
                                              dataReply.overdueReqsIssuesContent.itemType
                                                ? formatDataFieldLabel(dataReply.overdueReqsIssuesContent.itemType)
                                                : 'Name'
                                            }}
                                          </dt>
                                          <dd>
                                            @if (item.link) {
                                              @if (getInternalRoute(item.link); as route) {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [routerLink]="route"
                                                  (click)="onGuideLinkClick()"
                                                >
                                                  {{ item.name }}
                                                </a>
                                              } @else {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [href]="item.link"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {{ item.name }}
                                                </a>
                                              }
                                            } @else {
                                              {{ item.name }}
                                            }
                                          </dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ item.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Days overdue</dt>
                                          <dd>
                                            @if (item.daysOverdue != null) {
                                              <span class="ai-help-panel__data-card-overdue">{{ formatProjectDaysOverdue(item.daysOverdue) }}</span>
                                            } @else {
                                              —
                                            }
                                          </dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Due date</dt>
                                          <dd>{{ item.dueDate ? formatDailyHoursDateLabel(item.dueDate) : '—' }}</dd>
                                        </div>
                                        @if (item.project) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Project</dt>
                                            <dd>{{ item.project }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.overdueReqsIssuesContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  Showing a truncated list of overdue items.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'priority_tasks' && dataReply.priorityTasksContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (dataReply.priorityTasksContent.tasks.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (task of dataReply.priorityTasksContent.tasks; track task.name + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Task name</dt>
                                          <dd>{{ task.name }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ task.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Due date</dt>
                                          <dd>{{ task.dueDate ? formatDailyHoursDateLabel(task.dueDate) : '—' }}</dd>
                                        </div>
                                        @if (task.project) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Project</dt>
                                            <dd>{{ task.project }}</dd>
                                          </div>
                                        }
                                        @if (task.assignees) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Assignees</dt>
                                            <dd>{{ task.assignees }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.priorityTasksContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of priority tasks.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'my_pending_service_tasks' && dataReply.pendingServiceTasksContent) {
                            <div class="ai-help-panel__data-project-tasks">

                              @if (dataReply.pendingServiceTasksContent.tasks.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (
                                    task of dataReply.pendingServiceTasksContent.tasks;
                                    track task.taskId || task.taskName + '-' + $index
                                  ) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Task name</dt>
                                          <dd>{{ task.taskName }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Status</dt>
                                          <dd>{{ task.status || '—' }}</dd>
                                        </div>
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Due date</dt>
                                          <dd>{{ task.dueDate ? formatDailyHoursDateLabel(task.dueDate) : '—' }}</dd>
                                        </div>
                                        @if (task.projectName) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Project</dt>
                                            <dd>{{ task.projectName }}</dd>
                                          </div>
                                        }
                                        @if (task.serviceName) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Service</dt>
                                            <dd>{{ task.serviceName }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.pendingServiceTasksContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  Showing a truncated list of pending service tasks.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'items_due' && dataReply.itemsDueContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @for (group of dataReply.itemsDueContent.groups; track group.type) {
                                <div class="ai-help-panel__data-completed-group">
                                  <span class="ai-help-panel__data-project-tasks-label">
                                    {{ formatDataFieldLabel(group.type) }} ({{ group.items.length }})
                                  </span>
                                  <ul class="ai-help-panel__data-project-tasks-list">
                                    @for (item of group.items; track item.name + '-' + $index) {
                                      <li>
                                        <dl class="ai-help-panel__data-project-task">
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Name</dt>
                                            <dd>{{ item.name }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Status</dt>
                                            <dd>{{ item.status || '—' }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Due date</dt>
                                            <dd>{{ item.dueDate ? formatDailyHoursDateLabel(item.dueDate) : '—' }}</dd>
                                          </div>
                                          @if (item.project) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Project</dt>
                                              <dd>{{ item.project }}</dd>
                                            </div>
                                          }
                                          @if (item.assignees) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Assignees</dt>
                                              <dd>{{ item.assignees }}</dd>
                                            </div>
                                          }
                                        </dl>
                                      </li>
                                    }
                                  </ul>
                                </div>
                              }

                              @if (dataReply.itemsDueContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of due items.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'work_today' && dataReply.workTodayContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @for (section of dataReply.workTodayContent.groups; track section.key) {
                                <div class="ai-help-panel__data-completed-group">
                                  <span class="ai-help-panel__data-project-tasks-label">
                                    {{ section.label }} ({{ section.items.length }})
                                  </span>
                                  <ul class="ai-help-panel__data-project-tasks-list">
                                    @for (item of section.items; track workTodayItemTrackId(item, $index)) {
                                      <li>
                                        <dl class="ai-help-panel__data-project-task">
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Name</dt>
                                            <dd>
                                              @if (item.link) {
                                                @if (getInternalRoute(item.link); as route) {
                                                  <a
                                                    class="ai-help-panel__data-project-task-link"
                                                    [routerLink]="route"
                                                    (click)="onGuideLinkClick()"
                                                  >
                                                    {{ item.name }}
                                                  </a>
                                                } @else {
                                                  <a
                                                    class="ai-help-panel__data-project-task-link"
                                                    [href]="item.link"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    {{ item.name }}
                                                  </a>
                                                }
                                              } @else {
                                                {{ item.name }}
                                              }
                                            </dd>
                                          </div>
                                          @if (item.type) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Type</dt>
                                              <dd>{{ formatDataFieldLabel(item.type) }}</dd>
                                            </div>
                                          }
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Status</dt>
                                            <dd>{{ item.status || '—' }}</dd>
                                          </div>
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Due date</dt>
                                            <dd>{{ item.dueDate ? formatDailyHoursDateLabel(item.dueDate) : '—' }}</dd>
                                          </div>
                                          @if (item.project) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Project</dt>
                                              <dd>{{ item.project }}</dd>
                                            </div>
                                          }
                                          @if (item.daysOverdue != null) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Days overdue</dt>
                                              <dd>
                                                <span class="ai-help-panel__data-card-overdue">
                                                  {{ formatProjectDaysOverdue(item.daysOverdue) }}
                                                </span>
                                              </dd>
                                            </div>
                                          }
                                        </dl>
                                      </li>
                                    }
                                  </ul>
                                </div>
                              }

                              @if (
                                !dataReply.workTodayContent.doNow.length &&
                                !dataReply.workTodayContent.comingUp.length &&
                                !dataReply.intro
                              ) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  No work scheduled right now.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'updates' && dataReply.updatesContent) {
                            <div class="ai-help-panel__data-updates">
                              <div class="ai-help-panel__data-updates-summary">
                                @if (dataReply.updatesContent.user) {
                                  <span class="ai-help-panel__data-updates-user">User: {{ dataReply.updatesContent.user }}</span>
                                }
                                @if (dataReply.updatesContent.count != null) {
                                  <span class="ai-help-panel__data-updates-count">Updates: {{ dataReply.updatesContent.count }}</span>
                                }
                              </div>

                              @if (dataReply.updatesContent.updates?.length) {
                                <ul class="ai-help-panel__data-updates-list">
                                  @for (u of dataReply.updatesContent.updates; track $index) {
                                    <li>
                                      @if (dataReply.updatesContent.detail === true) {
                                        <div class="ai-help-panel__data-update-item ai-help-panel__data-update-item--static">
                                          <span class="ai-help-panel__data-update-item-title">
                                            {{ u.on ?? u.type ?? 'Update' }}
                                          </span>
                                          @if (u.date) {
                                            <span class="ai-help-panel__data-update-item-date">{{ u.date }}</span>
                                          }
                                          @if (u.text) {
                                            <span class="ai-help-panel__data-update-item-text">{{ u.text }}</span>
                                          }
                                        </div>
                                      } @else {
                                        <button
                                          type="button"
                                          class="ai-help-panel__data-update-item"
                                          [disabled]="!isDataSelectionEnabled(message.id)"
                                          (click)="onDataUpdateClick(u)"
                                        >
                                          <span class="ai-help-panel__data-update-item-title">
                                            {{ u.on ?? u.type ?? 'Update' }}
                                          </span>
                                          @if (u.date) {
                                            <span class="ai-help-panel__data-update-item-date">{{ u.date }}</span>
                                          }
                                          @if (u.text) {
                                            <span class="ai-help-panel__data-update-item-text">{{ u.text }}</span>
                                          }
                                        </button>
                                      }
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'comment_search' && dataReply.commentSearchContent) {
                            <div class="ai-help-panel__data-comments">
                              <div class="ai-help-panel__data-comments-summary">

                                @if (dataReply.commentSearchContent.total != null) {
                                  <span class="ai-help-panel__data-comments-count">
                                    Total: {{ dataReply.commentSearchContent.total }}
                                  </span>
                                }
                              </div>

                              @if (dataReply.commentSearchContent.matches.length) {
                                <ul class="ai-help-panel__data-comments-list">
                                  @for (match of dataReply.commentSearchContent.matches; track $index) {
                                    <li>
                                      <div class="ai-help-panel__data-comment-item">
                                        <span class="ai-help-panel__data-comment-item-title">
                                          {{ match.on || match.type || 'Comment' }}
                                        </span>
                                        <span class="ai-help-panel__data-comment-item-meta">
                                          @if (match.project) {
                                            <span>{{ match.project }}</span>
                                          }
                                          @if (match.by) {
                                            <span>{{ match.by }}</span>
                                          }
                                          @if (match.date) {
                                            <span>{{ formatDailyHoursDateLabel(match.date) }}</span>
                                          }
                                          @if (match.type && match.on && match.type !== match.on.toLowerCase()) {
                                            <span>{{ formatDataFieldLabel(match.type) }}</span>
                                          }
                                        </span>
                                        @if (match.snippet) {
                                          <span class="ai-help-panel__data-comment-item-snippet">{{ match.snippet }}</span>
                                        }
                                      </div>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.commentSearchContent.hasMore) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  Showing a truncated list of matching comments.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'my_approvals' && dataReply.myApprovalsContent) {
                            <div class="ai-help-panel__data-project-tasks ai-help-panel__data-project-tasks--approvals">
                              @if (dataReply.myApprovalsContent.total != null) {
                                <div class="ai-help-panel__data-metric" aria-label="Approvals total">
                                  <div class="ai-help-panel__data-metric-copy">
                                    <span class="ai-help-panel__data-metric-value">{{ dataReply.myApprovalsContent.total }}</span>
                                    <span class="ai-help-panel__data-metric-label">Total</span>
                                  </div>
                                  <span class="ai-help-panel__data-metric-icon" aria-hidden="true">
                                    <mat-icon>assignment_turned_in</mat-icon>
                                  </span>
                                </div>
                              }

                              @for (group of dataReply.myApprovalsContent.groups; track group.type) {
                                <div class="ai-help-panel__data-completed-group">
                                  <div class="ai-help-panel__data-type-row">
                                    <span class="ai-help-panel__data-project-tasks-label">
                                      {{ formatDataFieldLabel(group.type) }}
                                    </span>
                                    <span class="ai-help-panel__data-type-badge">{{ group.items.length }}</span>
                                  </div>
                                  <ul class="ai-help-panel__data-project-tasks-list">
                                    @for (item of group.items; track item.title + '-' + $index) {
                                      <li>
                                        <dl class="ai-help-panel__data-project-task">
                                          <div class="ai-help-panel__data-project-task-row ai-help-panel__data-project-task-row--title">
                                            <div class="ai-help-panel__data-project-task-title-block">
                                              <dt>Title</dt>
                                              <dd>
                                                @if (item.link) {
                                                  @if (getInternalRoute(item.link); as route) {
                                                    <a
                                                      class="ai-help-panel__data-project-task-link"
                                                      [routerLink]="route"
                                                      (click)="onGuideLinkClick()"
                                                    >
                                                      {{ item.title }}
                                                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                                                    </a>
                                                  } @else {
                                                    <a
                                                      class="ai-help-panel__data-project-task-link"
                                                      [href]="item.link"
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                    >
                                                      {{ item.title }}
                                                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                                                    </a>
                                                  }
                                                } @else {
                                                  {{ item.title }}
                                                }
                                              </dd>
                                            </div>
                                            <span class="ai-help-panel__data-status-pill">Pending</span>
                                          </div>
                                          @if (item.project) {
                                            <div class="ai-help-panel__data-project-task-row">
                                              <dt>Project</dt>
                                              <dd>{{ item.project }}</dd>
                                            </div>
                                          }
                                          @if (item.requestedOn) {
                                            <div class="ai-help-panel__data-project-task-row ai-help-panel__data-project-task-row--date">
                                              <dt>Requested on</dt>
                                              <dd>
                                                <mat-icon aria-hidden="true">calendar_today</mat-icon>
                                                {{ formatDailyHoursDateLabel(item.requestedOn) }}
                                              </dd>
                                            </div>
                                          }
                                        </dl>
                                      </li>
                                    }
                                  </ul>
                                </div>
                              }

                              @if (dataReply.myApprovalsContent.hasMore) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  More pending approvals exist.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'client_requests' && dataReply.clientRequestsContent) {
                            <div class="ai-help-panel__data-project-tasks">
                              @if (dataReply.clientRequestsContent.client || dataReply.clientRequestsContent.count != null) {
                                <div class="ai-help-panel__data-comments-summary">
                                  @if (dataReply.clientRequestsContent.client) {
                                    <span class="ai-help-panel__data-comments-count">
                                      Client: {{ dataReply.clientRequestsContent.client }}
                                    </span>
                                  }
                                  @if (dataReply.clientRequestsContent.count != null) {
                                    <span class="ai-help-panel__data-comments-count">
                                      Count: {{ dataReply.clientRequestsContent.count }}
                                    </span>
                                  }
                                </div>
                              }

                              @if (dataReply.clientRequestsContent.requests.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (item of dataReply.clientRequestsContent.requests; track item.request + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Request</dt>
                                          <dd>
                                            @if (item.link) {
                                              @if (getInternalRoute(item.link); as route) {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [routerLink]="route"
                                                  (click)="onGuideLinkClick()"
                                                >
                                                  {{ item.request }}
                                                </a>
                                              } @else {
                                                <a
                                                  class="ai-help-panel__data-project-task-link"
                                                  [href]="item.link"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {{ item.request }}
                                                </a>
                                              }
                                            } @else {
                                              {{ item.request }}
                                            }
                                          </dd>
                                        </div>
                                        @if (item.status) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Status</dt>
                                            <dd>{{ item.status }}</dd>
                                          </div>
                                        }
                                        @if (item.project) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Project</dt>
                                            <dd>{{ item.project }}</dd>
                                          </div>
                                        }
                                        @if (item.dueDate) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Due date</dt>
                                            <dd>{{ formatDailyHoursDateLabel(item.dueDate) }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.clientRequestsContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">
                                  Showing a truncated list of requests.
                                </p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'ranking' && dataReply.rankingContent) {
                            @if (dataReply.rankingContent.ranking.length) {
                              <div class="ai-help-panel__data-ranking">
                                <div class="ai-help-panel__data-ranking-summary">
                                  @if (dataReply.rankingContent.scope) {
                                    <span class="ai-help-panel__data-ranking-scope">{{ dataReply.rankingContent.scope }}</span>
                                  }
                                  @if (dataReply.rankingContent.peopleCounted != null) {
                                    <span class="ai-help-panel__data-ranking-count">
                                      {{ dataReply.rankingContent.countLabel ?? 'People counted' }}:
                                      {{ dataReply.rankingContent.peopleCounted }}
                                    </span>
                                  }
                                </div>

                                <ol class="ai-help-panel__data-ranking-list">
                                  @for (entry of dataReply.rankingContent.ranking; track entry.name + '-' + $index; let rank = $index) {
                                    <li>
                                      <div class="ai-help-panel__data-ranking-item">
                                        <span class="ai-help-panel__data-ranking-place" aria-hidden="true">{{ rank + 1 }}</span>
                                        <div class="ai-help-panel__data-ranking-body">
                                          <div class="ai-help-panel__data-ranking-row">
                                            <span class="ai-help-panel__data-ranking-name">{{ entry.name }}</span>
                                            <span class="ai-help-panel__data-ranking-hours">
                                              {{ formatRankingEntryValue(entry, dataReply.rankingContent.itemLabel) }}
                                            </span>
                                          </div>
                                          @if (entry.percent != null) {
                                            <div class="ai-help-panel__data-ranking-plot">
                                              <div
                                                class="ai-help-panel__data-progress-track"
                                                role="progressbar"
                                                [attr.aria-valuenow]="projectProgressWidth(entry.percent)"
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                                [attr.aria-label]="entry.name + ' share ' + formatProjectPercent(entry.percent)"
                                              >
                                                <div
                                                  class="ai-help-panel__data-progress-fill"
                                                  [style.width.%]="projectProgressWidth(entry.percent)"
                                                ></div>
                                              </div>
                                              <span class="ai-help-panel__data-ranking-percent">
                                                {{ formatProjectPercent(entry.percent) }}
                                              </span>
                                            </div>
                                          }
                                        </div>
                                      </div>
                                    </li>
                                  }
                                </ol>
                              </div>
                            }
                          } @else if (dataReply.dataKind === 'user' && dataReply.userContent) {
                            <div class="ai-help-panel__data-user">
                              <div class="ai-help-panel__data-user-summary">
                                @if (dataReply.userContent.user) {
                                  <span class="ai-help-panel__data-user-name">{{ dataReply.userContent.user }}</span>
                                }
                                @if (dataReply.userContent.totalHours != null) {
                                  <span class="ai-help-panel__data-user-total">
                                    Total: {{ formatRankingHours(dataReply.userContent.totalHours) }}
                                  </span>
                                }
                              </div>

                              @if (dataReply.userContent.byTask.length) {
                                <ul class="ai-help-panel__data-user-list">
                                  @for (entry of dataReply.userContent.byTask; track entry.item + '-' + $index) {
                                    <li>
                                      <div class="ai-help-panel__data-user-item">
                                        <div class="ai-help-panel__data-user-item-main">
                                          <span class="ai-help-panel__data-user-item-title">{{ entry.item }}</span>
                                          @if (entry.type) {
                                            <span class="ai-help-panel__data-user-item-type">{{ entry.type }}</span>
                                          }
                                        </div>
                                        <span class="ai-help-panel__data-user-item-hours">{{ formatRankingHours(entry.hours) }}</span>
                                      </div>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'daily_hours' && dataReply.dailyHoursContent) {
                            <div class="ai-help-panel__data-daily-hours">
                              <div class="ai-help-panel__data-daily-hours-summary">
                                @if (dataReply.dailyHoursContent.user) {
                                  <span class="ai-help-panel__data-daily-hours-user">{{ dataReply.dailyHoursContent.user }}</span>
                                }
                                @if (dataReply.dailyHoursContent.totalHours != null) {
                                  <span class="ai-help-panel__data-daily-hours-total">
                                    Total: {{ formatRankingHours(dataReply.dailyHoursContent.totalHours) }}
                                  </span>
                                }
                                @if (dataReply.dailyHoursContent.dayCount != null) {
                                  <span class="ai-help-panel__data-daily-hours-count">
                                    {{ dataReply.dailyHoursContent.dayCount }} days
                                  </span>
                                }
                              </div>

                              @if (dataReply.dailyHoursContent.days.length) {
                                <ul class="ai-help-panel__data-daily-hours-list">
                                  @for (entry of dataReply.dailyHoursContent.days; track entry.date + '-' + $index) {
                                    <li>
                                      <div class="ai-help-panel__data-daily-hours-item">
                                        <span class="ai-help-panel__data-daily-hours-date">{{ formatDailyHoursDateLabel(entry.date) }}</span>
                                        <span class="ai-help-panel__data-daily-hours-hours">{{ formatRankingHours(entry.hours) }}</span>
                                      </div>
                                    </li>
                                  }
                                </ul>
                              }

                              @if (dataReply.dailyHoursContent.truncated) {
                                <p class="ai-help-panel__data-daily-hours-truncated">Showing a truncated list of days.</p>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'ai_hours_trend' && dataReply.aiHoursTrendContent) {
                            <div class="ai-help-panel__data-ai-hours-trend">
                              <div class="ai-help-panel__data-daily-hours-summary">
                                @if (dataReply.aiHoursTrendContent.scopeLabel) {
                                  <span class="ai-help-panel__data-daily-hours-user">{{ dataReply.aiHoursTrendContent.scopeLabel }}</span>
                                }
                                @if (dataReply.aiHoursTrendContent.totalHours != null) {
                                  <span class="ai-help-panel__data-daily-hours-total">
                                    Total: {{ formatRankingHours(dataReply.aiHoursTrendContent.totalHours) }}
                                  </span>
                                }
                                @if (dataReply.aiHoursTrendContent.weeksCount != null) {
                                  <span class="ai-help-panel__data-daily-hours-count">
                                    {{ dataReply.aiHoursTrendContent.weeksCount }} weeks
                                  </span>
                                }
                              </div>

                              @if (dataReply.aiHoursTrendContent.weeks.length) {
                                <ul
                                  class="ai-help-panel__data-ai-hours-trend-chart"
                                  role="list"
                                  [attr.aria-label]="'AI hours trend over ' + (dataReply.aiHoursTrendContent.weeksCount ?? dataReply.aiHoursTrendContent.weeks.length) + ' weeks'"
                                >
                                  @for (week of dataReply.aiHoursTrendContent.weeks; track week.weekStart + '-' + $index) {
                                    <li class="ai-help-panel__data-ai-hours-trend-row">
                                      <span class="ai-help-panel__data-ai-hours-trend-label">{{ week.weekLabel }}</span>
                                      <div class="ai-help-panel__data-ai-hours-trend-bar-wrap">
                                        <div
                                          class="ai-help-panel__data-ai-hours-trend-bar"
                                          role="img"
                                          [attr.aria-label]="week.weekLabel + ': ' + formatRankingHours(week.hours)"
                                          [style.width.%]="aiHoursTrendBarWidth(week.hours, dataReply.aiHoursTrendContent.weeks)"
                                        ></div>
                                      </div>
                                      <span class="ai-help-panel__data-ai-hours-trend-hours">{{ formatRankingHours(week.hours) }}</span>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'leave' && dataReply.leaveContent) {
                            <div class="ai-help-panel__data-leave">
                              <div class="ai-help-panel__data-leave-summary">
                                @if (dataReply.leaveContent.scope) {
                                  <span class="ai-help-panel__data-leave-scope">{{ dataReply.leaveContent.scope }}</span>
                                }
                                @if (dataReply.leaveContent.peopleCounted != null) {
                                  <span class="ai-help-panel__data-leave-count">
                                    People counted: {{ dataReply.leaveContent.peopleCounted }}
                                  </span>
                                }
                              </div>

                              @if (dataReply.leaveContent.people.length) {
                                <ul class="ai-help-panel__data-leave-list">
                                  @for (person of dataReply.leaveContent.people; track person.name + '-' + $index) {
                                    <li>
                                      <div class="ai-help-panel__data-leave-person">
                                        <div class="ai-help-panel__data-leave-person-header">
                                          <span class="ai-help-panel__data-leave-person-name">{{ person.name }}</span>
                                          <span class="ai-help-panel__data-leave-person-hours">{{ formatRankingHours(person.totalHours) }}</span>
                                        </div>

                                        @if (person.days.length) {
                                          <ul class="ai-help-panel__data-leave-days">
                                            @for (day of person.days; track day.date + '-' + $index) {
                                              <li>
                                                <div class="ai-help-panel__data-leave-day">
                                                  <div class="ai-help-panel__data-leave-day-main">
                                                    <span class="ai-help-panel__data-leave-day-date">{{ formatDailyHoursDateLabel(day.date) }}</span>
                                                    @if (day.category) {
                                                      <span class="ai-help-panel__data-leave-day-category">{{ day.category }}</span>
                                                    }
                                                  </div>
                                                  <span class="ai-help-panel__data-leave-day-hours">{{ formatRankingHours(day.hours) }}</span>
                                                </div>
                                              </li>
                                            }
                                          </ul>
                                        }
                                      </div>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'free_hours' && dataReply.freeHoursContent) {
                            <div class="ai-help-panel__data-free-hours">
                              <div class="ai-help-panel__data-free-hours-summary">
                                @if (dataReply.freeHoursContent.target) {
                                  <span class="ai-help-panel__data-free-hours-target">
                                    {{ dataReply.freeHoursContent.target }}
                                  </span>
                                }
                              
                              </div>

                              @if (dataReply.freeHoursContent.people.length) {
                                <div class="ai-help-panel__data-free-hours-legend" aria-hidden="true">
                                  <span class="ai-help-panel__data-free-hours-legend-item ai-help-panel__data-free-hours-legend-item--scheduled">
                                    Scheduled
                                  </span>
                                  <span class="ai-help-panel__data-free-hours-legend-item ai-help-panel__data-free-hours-legend-item--free">
                                    Free
                                  </span>
                                </div>

                                <ul
                                  class="ai-help-panel__data-free-hours-list"
                                  role="list"
                                  [attr.aria-label]="'Free hours for ' + (dataReply.freeHoursContent.target || 'people')"
                                >
                                  @for (person of dataReply.freeHoursContent.people; track person.name + '-' + $index) {
                                    <li>
                                      <div class="ai-help-panel__data-free-hours-person">
                                        <div class="ai-help-panel__data-free-hours-person-header">
                                          <span class="ai-help-panel__data-free-hours-person-name">{{ person.name }}</span>
                                          <span class="ai-help-panel__data-free-hours-person-values">
                                            {{ formatRankingHours(person.freeHours) }} free
                                            · {{ formatRankingHours(person.scheduledHours) }} scheduled
                                          </span>
                                        </div>

                                        <div
                                          class="ai-help-panel__data-free-hours-plot"
                                          role="img"
                                          [attr.aria-label]="
                                            person.name +
                                            ': scheduled ' +
                                            formatRankingHours(person.scheduledHours) +
                                            ', free ' +
                                            formatRankingHours(person.freeHours)
                                          "
                                        >
                                          <div class="ai-help-panel__data-free-hours-track">
                                            <div
                                              class="ai-help-panel__data-free-hours-fill ai-help-panel__data-free-hours-fill--scheduled"
                                              [style.width.%]="
                                                freeHoursScheduledWidth(
                                                  person.scheduledHours,
                                                  dataReply.freeHoursContent.capacityHours
                                                )
                                              "
                                            ></div>
                                            <div
                                              class="ai-help-panel__data-free-hours-fill ai-help-panel__data-free-hours-fill--free"
                                              [style.width.%]="
                                                freeHoursAvailableWidth(
                                                  person.scheduledHours,
                                                  person.freeHours,
                                                  dataReply.freeHoursContent.capacityHours
                                                )
                                              "
                                            ></div>
                                          </div>
                                        </div>
                                      </div>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else if (dataReply.dataKind === 'client_brief') {
                            @if (dataReply.clientBriefContent; as brief) {
                            <div class="ai-help-panel__client-brief">
                              <header class="ai-help-panel__client-brief-header">
                                <div class="ai-help-panel__client-brief-header-main">
                                  <span class="ai-help-panel__client-brief-icon" aria-hidden="true">
                                    <mat-icon>business_center</mat-icon>
                                  </span>
                                  <div class="ai-help-panel__client-brief-heading">
                                    <h4 class="ai-help-panel__client-brief-title">
                                      {{ brief.client || dataReply.intro || 'Account brief' }}
                                    </h4>
                                    <p class="ai-help-panel__client-brief-subtitle">Account brief</p>
                                  </div>
                                </div>
                                @if (brief.monthLabel) {
                                  <span class="ai-help-panel__client-brief-period">{{ brief.monthLabel }}</span>
                                }
                              </header>

                              @if (
                                brief.activeProjectCount != null ||
                                brief.overdueTaskCount != null ||
                                brief.hoursThisMonth != null
                              ) {
                                <div class="ai-help-panel__client-brief-kpis" aria-label="Account summary">
                                  @if (brief.activeProjectCount != null) {
                                    <div class="ai-help-panel__client-brief-kpi ai-help-panel__client-brief-kpi--projects">
                                      <span class="ai-help-panel__client-brief-kpi-accent" aria-hidden="true"></span>
                                      <span class="ai-help-panel__client-brief-kpi-icon" aria-hidden="true">
                                        <mat-icon>folder_open</mat-icon>
                                      </span>
                                      <span class="ai-help-panel__client-brief-kpi-value">{{ brief.activeProjectCount }}</span>
                                      <span class="ai-help-panel__client-brief-kpi-label">Active projects</span>
                                    </div>
                                  }
                                  @if (brief.overdueTaskCount != null) {
                                    <div
                                      class="ai-help-panel__client-brief-kpi ai-help-panel__client-brief-kpi--overdue"
                                      [class.ai-help-panel__client-brief-kpi--alert]="brief.overdueTaskCount > 0"
                                    >
                                      <span class="ai-help-panel__client-brief-kpi-accent" aria-hidden="true"></span>
                                      <span class="ai-help-panel__client-brief-kpi-icon" aria-hidden="true">
                                        <mat-icon>warning_amber</mat-icon>
                                      </span>
                                      <span class="ai-help-panel__client-brief-kpi-value">{{ brief.overdueTaskCount }}</span>
                                      <span class="ai-help-panel__client-brief-kpi-label">Overdue tasks</span>
                                    </div>
                                  }
                                  @if (brief.hoursThisMonth != null) {
                                    <div class="ai-help-panel__client-brief-kpi ai-help-panel__client-brief-kpi--hours">
                                      <span class="ai-help-panel__client-brief-kpi-accent" aria-hidden="true"></span>
                                      <span class="ai-help-panel__client-brief-kpi-icon" aria-hidden="true">
                                        <mat-icon>schedule</mat-icon>
                                      </span>
                                      <span class="ai-help-panel__client-brief-kpi-value">{{ formatRankingHours(brief.hoursThisMonth) }}</span>
                                      <span class="ai-help-panel__client-brief-kpi-label">Hours logged</span>
                                    </div>
                                  }
                                </div>
                              }

                              @if (brief.projects.length) {
                                <section class="ai-help-panel__client-brief-section" aria-label="Projects">
                                  <div class="ai-help-panel__client-brief-section-head">
                                    <h5 class="ai-help-panel__client-brief-section-title">
                                      <mat-icon aria-hidden="true">folder_open</mat-icon>
                                      Projects
                                    </h5>
                                    <span class="ai-help-panel__client-brief-section-count">{{ brief.projects.length }}</span>
                                  </div>

                                  <ul class="ai-help-panel__client-brief-projects">
                                    @for (project of brief.projects; track project.projectName + '-' + $index) {
                                      <li>
                                        <article class="ai-help-panel__client-brief-project">
                                          <div class="ai-help-panel__client-brief-project-top">
                                            <span class="ai-help-panel__client-brief-project-name">{{ project.projectName }}</span>
                                            @if (project.status) {
                                              <span
                                                class="ai-help-panel__client-brief-status"
                                                [ngClass]="clientBriefProjectStatusClass(project.status)"
                                              >
                                                {{ project.status }}
                                              </span>
                                            }
                                          </div>

                                          @if (project.percentComplete != null) {
                                            <div class="ai-help-panel__client-brief-project-progress">
                                              <span class="ai-help-panel__client-brief-project-percent">
                                                {{ formatProjectPercent(project.percentComplete) }}
                                              </span>
                                              <div
                                                class="ai-help-panel__client-brief-progress-track"
                                                role="progressbar"
                                                [attr.aria-valuenow]="projectProgressWidth(project.percentComplete)"
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                                [attr.aria-label]="project.projectName + ' progress'"
                                              >
                                                <div
                                                  class="ai-help-panel__client-brief-progress-fill"
                                                  [ngClass]="'ai-help-panel__client-brief-progress-fill--' + clientBriefProgressTone(project.percentComplete)"
                                                  [style.width.%]="projectProgressWidth(project.percentComplete)"
                                                ></div>
                                              </div>
                                            </div>
                                          }

                                          @if (project.waitingOn) {
                                            <p class="ai-help-panel__client-brief-waiting">
                                              <mat-icon aria-hidden="true">hourglass_empty</mat-icon>
                                              Waiting on {{ project.waitingOn }}
                                            </p>
                                          }
                                        </article>
                                      </li>
                                    }
                                  </ul>
                                </section>
                              }

                              @if (brief.recentActivity.length) {
                                <section class="ai-help-panel__client-brief-section ai-help-panel__client-brief-section--activity" aria-label="Recent activity">
                                  <div class="ai-help-panel__client-brief-section-head">
                                    <h5 class="ai-help-panel__client-brief-section-title ai-help-panel__client-brief-section-title--activity">
                                      <mat-icon aria-hidden="true">ssid_chart</mat-icon>
                                      Recent activity
                                    </h5>
                                  </div>

                                  <ul class="ai-help-panel__client-brief-timeline">
                                    @for (activity of brief.recentActivity; track $index) {
                                      @if (parseClientBriefActivity(activity); as item) {
                                        <li>
                                          <div class="ai-help-panel__client-brief-timeline-body">
                                            @if (item.date) {
                                              <span class="ai-help-panel__client-brief-timeline-date">{{ item.date }}</span>
                                            }
                                            <p class="ai-help-panel__client-brief-timeline-text">{{ item.text }}</p>
                                          </div>
                                        </li>
                                      }
                                    }
                                  </ul>
                                </section>
                              }

                              @if (brief.talkingPoints.length) {
                                <section
                                  class="ai-help-panel__client-brief-section ai-help-panel__client-brief-section--points"
                                  aria-label="Talking points"
                                >
                                  <div class="ai-help-panel__client-brief-section-head">
                                    <h5 class="ai-help-panel__client-brief-section-title ai-help-panel__client-brief-section-title--points">
                                      <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon>
                                      Talking points
                                    </h5>
                                  </div>

                                  <ol class="ai-help-panel__client-brief-points">
                                    @for (point of brief.talkingPoints; track $index; let pointIndex = $index) {
                                      <li>
                                        <span class="ai-help-panel__client-brief-point-index" aria-hidden="true">{{ pointIndex + 1 }}</span>
                                        <p class="ai-help-panel__client-brief-point-text">{{ point }}</p>
                                      </li>
                                    }
                                  </ol>
                                </section>
                              }
                            </div>
                            }
                          } @else if (dataReply.dataKind === 'timesheet_submission' && dataReply.timesheetSubmissionContent) {
                            <div class="ai-help-panel__data-timesheet-submission">
                              <div class="ai-help-panel__data-leave-summary">
                                @if (dataReply.timesheetSubmissionContent.team) {
                                  <span class="ai-help-panel__data-leave-scope">{{ dataReply.timesheetSubmissionContent.team }}</span>
                                }
                                @if (dataReply.timesheetSubmissionContent.week; as week) {
                                  <span class="ai-help-panel__data-leave-count">
                                    {{ formatDailyHoursDateLabel(week.from) }} – {{ formatDailyHoursDateLabel(week.to) }}
                                  </span>
                                }
                              </div>

                              @if (
                                dataReply.timesheetSubmissionContent.totalMembers != null ||
                                dataReply.timesheetSubmissionContent.submittedCount != null ||
                                dataReply.timesheetSubmissionContent.notSubmittedCount != null
                              ) {
                                <div class="ai-help-panel__data-health-hours" aria-label="Submission counts">
                                  @if (dataReply.timesheetSubmissionContent.totalMembers != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Total</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.timesheetSubmissionContent.totalMembers }}</span>
                                    </div>
                                  }
                                  @if (dataReply.timesheetSubmissionContent.submittedCount != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Submitted</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.timesheetSubmissionContent.submittedCount }}</span>
                                    </div>
                                  }
                                  @if (dataReply.timesheetSubmissionContent.notSubmittedCount != null) {
                                    <div class="ai-help-panel__data-health-hour">
                                      <span class="ai-help-panel__data-health-hour-label">Not submitted</span>
                                      <span class="ai-help-panel__data-health-hour-value">{{ dataReply.timesheetSubmissionContent.notSubmittedCount }}</span>
                                    </div>
                                  }
                                </div>
                              }

                              @if (dataReply.timesheetSubmissionContent.notSubmitted.length) {
                                <ul class="ai-help-panel__data-project-tasks-list">
                                  @for (person of dataReply.timesheetSubmissionContent.notSubmitted; track person.name + '-' + $index) {
                                    <li>
                                      <dl class="ai-help-panel__data-project-task">
                                        <div class="ai-help-panel__data-project-task-row">
                                          <dt>Name</dt>
                                          <dd>{{ person.name }}</dd>
                                        </div>
                                        @if (person.status) {
                                          <div class="ai-help-panel__data-project-task-row">
                                            <dt>Status</dt>
                                            <dd>{{ person.status }}</dd>
                                          </div>
                                        }
                                      </dl>
                                    </li>
                                  }
                                </ul>
                              }
                            </div>
                          } @else {
                            @if (dataFieldEntries(dataReply); as fields) {
                              @if (fields.length) {
                                @if (asCountClientMetric(fields); as metric) {
                                  <div class="ai-help-panel__data-blocks ai-help-panel__data-blocks--metric">
                                    <div class="ai-help-panel__data-metric" aria-label="Count">
                                      <div class="ai-help-panel__data-metric-copy">
                                        <span class="ai-help-panel__data-metric-value">{{ metric.count }}</span>
                                        <span class="ai-help-panel__data-metric-label">{{ metric.label }}</span>
                                      </div>
                                      <span class="ai-help-panel__data-metric-icon" aria-hidden="true">
                                        <mat-icon>folder_open</mat-icon>
                                      </span>
                                    </div>
                                    @if (metric.client) {
                                      <div class="ai-help-panel__data-metric-footer">
                                        <span class="ai-help-panel__data-metric-footer-label">Client</span>
                                        <span class="ai-help-panel__data-client-chip">
                                          <span class="ai-help-panel__data-client-chip-avatar" aria-hidden="true">
                                            {{ clientInitial(metric.client) }}
                                          </span>
                                          <span class="ai-help-panel__data-client-chip-name">{{ metric.client }}</span>
                                        </span>
                                      </div>
                                    }
                                  </div>
                                } @else {
                                  <dl class="ai-help-panel__data-fields">
                                    @for (field of fields; track field.key) {
                                      <div class="ai-help-panel__data-field">
                                        <dt>{{ formatDataFieldLabel(field.key) }}</dt>
                                        <dd>{{ field.value }}</dd>
                                      </div>
                                    }
                                  </dl>
                                }
                              }
                            }
                          }

                          @if (dataReply.actions?.length) {
                            <div class="ai-help-panel__data-actions" role="group" aria-label="Reply actions">
                              @for (replyAction of dataReply.actions; track replyAction.action + '-' + $index) {
                                <button
                                  type="button"
                                  class="ai-help-panel__data-action-btn"
                                  [class.ai-help-panel__data-action-btn--primary]="
                                    replyAction.action === 'download_pdf' ||
                                    replyAction.action === 'confirm_send_email'
                                  "
                                  [disabled]="isDataReplyActionBusy(replyAction, dataReply)"
                                  (click)="onDataReplyActionClick(replyAction, dataReply, $event)"
                                >
                                  <mat-icon aria-hidden="true">{{ dataReplyActionIcon(replyAction) }}</mat-icon>
                                  <span>{{ dataReplyActionLabel(replyAction, dataReply) }}</span>
                                </button>
                              }
                            </div>
                          }
                        </div>
                      } @else {
                        @if (toConfirmContent(message.messageContent); as confirmData) {
                          <div class="ai-help-panel__confirm">
                            <p class="ai-help-panel__confirm-text">{{ confirmData.text }}</p>

                            <dl class="ai-help-panel__confirm-summary">
                              @for (field of confirmSummaryEntries(confirmData); track field.key) {
                                <div class="ai-help-panel__confirm-field">
                                  <dt>{{ formatDataFieldLabel(field.key) }}</dt>
                                  <dd>{{ field.value }}</dd>
                                </div>
                              }
                            </dl>

                            @if (hasConfirmSummaryList(confirmData, 'entries')) {
                              <div class="ai-help-panel__data-user">
                                <div class="ai-help-panel__data-user-summary">
                                  <span class="ai-help-panel__data-user-total">Entries</span>
                                  @if (confirmSummaryScalarValue(confirmData, 'total_hours'); as totalHours) {
                                    <span class="ai-help-panel__data-user-total">Total: {{ totalHours }}</span>
                                  }
                                </div>

                                <ul class="ai-help-panel__data-user-list">
                                  @for (entry of confirmSummaryList(confirmData, 'entries'); track $index) {
                                    <li class="ai-help-panel__data-user-item">
                                      <div class="ai-help-panel__data-user-item-main">
                                        <span class="ai-help-panel__data-user-item-name">
                                          {{ $any(entry)['item'] }}
                                        </span>
                                        @if ($any(entry)['hours'] != null) {
                                          <span class="ai-help-panel__data-user-item-hours">
                                            {{ formatRankingHours($any(entry)['hours']) }}
                                          </span>
                                        }
                                      </div>

                                      @if ($any(entry)['type']) {
                                        <div class="ai-help-panel__data-user-item-type">
                                          {{ formatDataFieldLabel($any(entry)['type']) }}
                                        </div>
                                      }
                                    </li>
                                  }
                                </ul>
                              </div>
                            }

                            <div class="ai-help-panel__confirm-actions">
                              <button
                                type="button"
                                class="ai-help-panel__confirm-btn ai-help-panel__confirm-btn--yes"
                                [disabled]="!isConfirmSelectionEnabled(message.id)"
                                (click)="onConfirmClick(message.id, true)"
                              >
                                <mat-icon aria-hidden="true">check_circle</mat-icon>
                                Yes, confirm
                              </button>
                              <button
                                type="button"
                                class="ai-help-panel__confirm-btn ai-help-panel__confirm-btn--no"
                                [disabled]="!isConfirmSelectionEnabled(message.id)"
                                (click)="onConfirmClick(message.id, false)"
                              >
                                <mat-icon aria-hidden="true">close</mat-icon>
                                No, cancel
                              </button>
                            </div>
                          </div>
                        } @else {
                          @if (toAskContent(message.messageContent); as ask) {
                            <div class="ai-help-panel__ask">
                              <p class="ai-help-panel__ask-text">{{ ask.text }}</p>

                              @if (ask.inputType === 'date' && isAskSelectionEnabled(message.id)) {
                                @let calendarConfig = getAskCalendarConfig(message.id, ask);
                                <div class="ai-help-panel__ask-date">
                                  <div class="ai-help-panel__ask-date-label">
                                    <mat-icon aria-hidden="true">calendar_today</mat-icon>
                                    <span>Select a date</span>
                                  </div>
                                  <mat-calendar
                                    class="ai-help-panel__ask-calendar"
                                    [selected]="askSelectedDate(message.id)"
                                    [startAt]="calendarConfig.startAt"
                                    [minDate]="calendarConfig.min"
                                    [maxDate]="calendarConfig.max"
                                    (selectedChange)="onAskDateSelected(message.id, $event)"
                                  ></mat-calendar>
                                </div>
                              }
                            </div>
                          } @else {
                        @if (toClarifyContent(message.messageContent); as clarify) {
                          <div class="ai-help-panel__clarify">
                            <p class="ai-help-panel__clarify-text">{{ clarify.text }}</p>

                            @if (clarify.items?.length) {
                              <ul class="ai-help-panel__clarify-items">
                                @for (item of clarify.items; track clarifyItemTrackId(item, $index)) {
                                  <li>
                                    <button
                                      type="button"
                                      class="ai-help-panel__clarify-item"
                                      [disabled]="!isClarifySelectionEnabled(message.id)"
                                      (click)="onClarifyItemClick(item)"
                                    >
                                      @if (item.kind === 'project') {
                                        <span class="ai-help-panel__clarify-item-type">Project</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.projectName }}</span>
                                        <span class="ai-help-panel__clarify-item-meta">
                                          @if (item.status) {
                                            <span>{{ item.status }}</span>
                                          }
                                        </span>
                                      } @else if (item.kind === 'person') {
                                        <span class="ai-help-panel__clarify-item-type">Person</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                        @if (item.job) {
                                          <span class="ai-help-panel__clarify-item-meta">
                                            <span>{{ item.job }}</span>
                                          </span>
                                        }
                                      } @else if (item.kind === 'template') {
                                        <span class="ai-help-panel__clarify-item-type">Template</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                      } @else if (item.kind === 'approval') {
                                        <span class="ai-help-panel__clarify-item-type">Approval</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                      } @else if (item.kind === 'service') {
                                        <span class="ai-help-panel__clarify-item-type">Service</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                      } @else if (item.kind === 'service_category') {
                                        <span class="ai-help-panel__clarify-item-type">Category</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                      } @else {
                                        <span class="ai-help-panel__clarify-item-type">Task</span>
                                        <span class="ai-help-panel__clarify-item-title">{{ item.taskName }}</span>
                                        <span class="ai-help-panel__clarify-item-meta">
                                          @if (item.project) {
                                            <span>{{ item.project }}</span>
                                          }
                                          @if (item.refNo != null) {
                                            <span>#{{ item.refNo }}</span>
                                          }
                                          @if (item.plannedCompletion) {
                                            <span>{{ formatDailyHoursDateLabel(item.plannedCompletion) }}</span>
                                          } @else if (item.due) {
                                            <span>{{ formatDailyHoursDateLabel(item.due) }}</span>
                                          }
                                        </span>
                                      }
                                    </button>
                                  </li>
                                }
                              </ul>
                            } @else if (clarify.choices?.length) {
                              <ul class="ai-help-panel__clarify-items">
                                @for (choice of clarify.choices; track $index + '-' + choice) {
                                  <li>
                                    <button
                                      type="button"
                                      class="ai-help-panel__clarify-item"
                                      [disabled]="!isClarifyOptionsListEnabled(message.id)"
                                      (click)="onClarifyChoiceClick(choice)"
                                    >
                                      <span class="ai-help-panel__clarify-item-title">{{ choice }}</span>
                                    </button>
                                  </li>
                                }
                              </ul>
                            } @else if (clarify.options?.length) {
                              @if (selectedClarifyGroup(message.id); as selectedGroup) {
                                <div class="ai-help-panel__clarify-items-header">
                                  <button
                                    type="button"
                                    class="ai-help-panel__clarify-back"
                                    [disabled]="!isClarifyOptionsListEnabled(message.id)"
                                    (click)="clearClarifyGroup(message.id)"
                                  >
                                    <mat-icon aria-hidden="true">arrow_back</mat-icon>
                                    <span>{{ selectedGroup }}</span>
                                  </button>
                                </div>

                                <ul class="ai-help-panel__clarify-items">
                                  @for (
                                    item of clarifyGroupItems(clarify, selectedGroup);
                                    track clarifyItemTrackId(item, $index)
                                  ) {
                                    <li>
                                      <button
                                        type="button"
                                        class="ai-help-panel__clarify-item"
                                        [disabled]="!isClarifyOptionsListEnabled(message.id)"
                                        (click)="onClarifyItemClick(item)"
                                      >
                                        @if (item.kind === 'project') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.projectName }}</span>
                                          <span class="ai-help-panel__clarify-item-meta">
                                            @if (item.status) {
                                              <span>{{ item.status }}</span>
                                            }
                                          </span>
                                        } @else if (item.kind === 'person') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                          @if (item.job) {
                                            <span class="ai-help-panel__clarify-item-meta">
                                              <span>{{ item.job }}</span>
                                            </span>
                                          }
                                        } @else if (item.kind === 'template') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                        } @else if (item.kind === 'approval') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                        } @else if (item.kind === 'service') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                        } @else if (item.kind === 'service_category') {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.name }}</span>
                                        } @else {
                                          <span class="ai-help-panel__clarify-item-title">{{ item.taskName }}</span>
                                          <span class="ai-help-panel__clarify-item-meta">
                                            @if (item.project) {
                                              <span>{{ item.project }}</span>
                                            }
                                            @if (item.refNo != null) {
                                              <span>#{{ item.refNo }}</span>
                                            }
                                            @if (item.plannedCompletion) {
                                              <span>{{ formatDailyHoursDateLabel(item.plannedCompletion) }}</span>
                                            } @else if (item.due) {
                                              <span>{{ formatDailyHoursDateLabel(item.due) }}</span>
                                            }
                                          </span>
                                        }
                                      </button>
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <ul class="ai-help-panel__clarify-groups">
                                  @for (option of clarify.options; track option.group) {
                                    <li>
                                      <button
                                        type="button"
                                        class="ai-help-panel__clarify-group"
                                        [disabled]="!isClarifyOptionsListEnabled(message.id)"
                                        (click)="selectClarifyGroup(message.id, option.group)"
                                      >
                                        <span class="ai-help-panel__clarify-group-label">{{ option.group }}</span>
                                        <span class="ai-help-panel__clarify-group-count">{{ option.items.length }}</span>
                                        <mat-icon aria-hidden="true">chevron_right</mat-icon>
                                      </button>
                                    </li>
                                  }
                                </ul>
                              }
                            }

                            @if (toClarifyContent(message.messageContent)?.note; as clarifyNote) {
                              <p class="ai-help-panel__clarify-note">{{ clarifyNote }}</p>
                            }
                          </div>
                        } @else {
                          @if (toOffTopicContent(message.messageContent); as offTopic) {
                            <div class="ai-help-panel__off-topic">
                              <p class="ai-help-panel__off-topic-text">{{ offTopic.text }}</p>
                              @if (offTopic.action; as replyAction) {
                                <button
                                  type="button"
                                  class="ai-help-panel__off-topic-btn"
                                  (click)="onReplyActionClick(replyAction, $event)"
                                >
                                  <mat-icon aria-hidden="true">auto_awesome</mat-icon>
                                  <span>{{ replyAction.label }}</span>
                                </button>
                              }
                            </div>
                          } @else {
                            <p>{{ message.content }}</p>
                          }
                        }
                          }
                        }
                      }
                    }
                  }
                </div>
                @if (message.role === 'user') {
                  @if (shouldShowMessageAvatar(i)) {
                    <div
                      class="ai-help-panel__message-avatar ai-help-panel__message-avatar--user"
                      [attr.aria-label]="userDisplayName()"
                    >
                      @if (showUserAvatarImage()) {
                        <img
                          class="ai-help-panel__message-avatar-photo"
                          [src]="userAvatarUrl()"
                          alt=""
                          width="36"
                          height="36"
                          decoding="async"
                          draggable="false"
                          (error)="onUserAvatarError()"
                        />
                      } @else {
                        <span
                          class="ai-help-panel__message-avatar-initials"
                          [style.background-color]="userAvatarColor()"
                          aria-hidden="true"
                        >
                          {{ userAvatarInitials() }}
                        </span>
                      }
                    </div>
                  } @else {
                    <div
                      class="ai-help-panel__message-avatar ai-help-panel__message-avatar--user ai-help-panel__message-avatar--spacer"
                      aria-hidden="true"
                    ></div>
                  }
                }
              </article>
            }
          </div>
        }
      </div>
    </div>

    <footer class="ai-help-panel__composer">
      <div class="ai-help-panel__mode-tabs" role="tablist" aria-label="AI help modes">
        @for (tab of home.tabs; track tab.key) {
          <button
            type="button"
            role="tab"
            class="ai-help-panel__mode-tab"
            [class.ai-help-panel__mode-tab--active]="activeHomeTab() === tab.key"
            [attr.aria-selected]="activeHomeTab() === tab.key"
            (click)="onHomeTabClick(tab.key)"
          >
            {{ tab.label }}
          </button>
        }
      </div>

      <div
        class="ai-help-panel__composer-row"
        (click)="onComposerShellClick($event)"
      >
      <div class="form_field_wrapper ai-help-panel__composer-field">
        <textarea
          #composerInput
          class="form-control ai-help-panel__input"
            rows="1"
          placeholder="Ask WorkLuge Assistant or tell it what to do…"
          [ngModel]="state.draft()"
          (ngModelChange)="onDraftChange($event)"
            (input)="onComposerInput()"
          (keydown)="onComposerKeydown($event)"
            [attr.aria-busy]="state.isLoading()"
          aria-label="Message WorkLuge Assistant"
        ></textarea>
      </div>
      <div class="ai-help-panel__composer-actions">
        @if (speech.isSupported()) {
          <button
            type="button"
            class="ai-help-panel__voice-btn"
            [class.ai-help-panel__voice-btn--listening]="speech.isListening()"
            [class.ai-help-panel__voice-btn--processing]="speech.isProcessing()"
            [disabled]="state.isLoading() || speech.isProcessing()"
            [attr.aria-pressed]="speech.isListening()"
            [attr.aria-busy]="speech.isProcessing()"
            [matTooltip]="voiceInputTooltip()"
            matTooltipAlways
            [attr.aria-label]="voiceInputTooltip()"
            (click)="onToggleVoiceInput()"
          >
            @if (speech.isProcessing()) {
              <app-spinner class="ai-help-panel__voice-processing" [size]="18"></app-spinner>
            } @else if (speech.isListening()) {
              <span class="ai-help-panel__voice-waves" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </span>
              <mat-icon class="ai-help-panel__voice-stop">stop</mat-icon>
            } @else {
              <mat-icon>mic</mat-icon>
            }
          </button>
        }
        <button
          type="button"
          class="btn-save ai-help-panel__send-btn"
          [disabled]="!state.canSend()"
          aria-label="Send message"
          (click)="onSend()"
        >
            <mat-icon aria-hidden="true">send</mat-icon>
        </button>
        </div>
      </div>
      @if (speech.error(); as voiceError) {
        <p class="ai-help-panel__voice-error" role="alert">{{ voiceError }}</p>
      }
      
      <p class="ai-help-panel__disclaimer">
        <mat-icon class="ai-help-panel__disclaimer-icon" aria-hidden="true">info</mat-icon>
        <span>AI can make mistakes. Review before acting.</span>
        <span class="ai-help-panel__disclaimer-sep" aria-hidden="true"></span>
        <a
          class="ai-help-panel__privacy-link"
          [href]="privacyPageUrl"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Privacy policy. Opens in a new tab"
          (click)="onPrivacyPolicyClick($event)"
        >Privacy policy</a>
      </p>
    </footer>
    <!-- <div class="privacy-note-container">
    <p class="privacy-note">
      WorkLuge uses the information you provide to support your AI help experience. Check out our
      <a routerLink="/privacy" class="ai-help-panel__privacy-link">privacy policy</a>
      here.
    </p>
    </div> -->
  </div>
</div>
</div>

<app-confirm-popup
  [appendToBody]="true"
  [show]="showHistoryDeleteConfirm()"
  [config]="historyDeleteConfirmConfig()"
  (confirm)="onConfirmHistoryDelete()"
  (cancel)="onCancelHistoryDelete()"
></app-confirm-popup>
